<template>
  <div class="tw-page">
    <SnackbarComp :SnackbarComponent="snackbar" />

    <CreateCampaignDialog
      v-model="showEditDialog"
      :campaign="campaign"
      @saved="onCampaignEdited"
      @error="(msg) => showSnack('error', msg)"
    />

    <BulkAssignLeadsDialog
      v-model="showBulkAssignDialog"
      :campaign-id="campaignId"
      :leads="filteredLeads"
      :agents="assignableAgents"
      :contexts="campaignContexts"
      @saved="onBulkAssignSaved"
      @error="(msg) => showSnack('error', msg)"
    />

    <BulkUpdateCampaignLeadStatusDialog
      v-model="showBulkStatusDialog"
      :campaign-id="campaignId"
      :leads="leads"
      :agents="assignableAgents"
      :contexts="campaignContexts"
      @saved="onBulkStatusSaved"
      @error="(msg) => showSnack('error', msg)"
    />

    <LeadFormDialog
      v-model="showCreateLeadDialog"
      mode="CREATE"
      :preset-context-names="campaignContextNames"
      :save-fn="saveCampaignLead"
      @saved="onCampaignLeadCreated"
    />

    <RunAiCallsResultDialog
      v-model="showRunAiCallsResult"
      :result="runAiCallsResult"
    />

    <header class="tw-topbar">
      <button type="button" class="tw-topbar__back" aria-label="Back to campaigns" @click="goBack">
        <v-icon icon="mdi-arrow-left" size="18" />
      </button>
      <div class="tw-topbar__copy">
        <h1 class="tw-topbar__title">{{ campaign?.name || 'Campaign workspace' }}</h1>
      </div>

      <div class="tw-topbar__tabs" role="tablist" aria-label="Campaign sections">
        <button
          type="button"
          class="tw-topbar__tab"
          :class="{ 'is-active': pageTab === 'leads' }"
          role="tab"
          :aria-selected="pageTab === 'leads'"
          @click="pageTab = 'leads'"
        >
          Leads
        </button>
        <button
          type="button"
          class="tw-topbar__tab"
          :class="{ 'is-active': pageTab === 'dashboard' }"
          role="tab"
          :aria-selected="pageTab === 'dashboard'"
          @click="pageTab = 'dashboard'"
        >
          Dashboard
        </button>
        <button
          type="button"
          class="tw-topbar__tab"
          :class="{ 'is-active': pageTab === 'callHistory' }"
          role="tab"
          :aria-selected="pageTab === 'callHistory'"
          @click="pageTab = 'callHistory'"
        >
          Call History
        </button>
        <button
          type="button"
          class="tw-topbar__tab"
          :class="{ 'is-active': pageTab === 'dialAttempts' }"
          role="tab"
          :aria-selected="pageTab === 'dialAttempts'"
          @click="pageTab = 'dialAttempts'"
        >
          Dial Attempts
        </button>
        <button
          v-if="isAdmin"
          type="button"
          class="tw-topbar__tab"
          :class="{ 'is-active': pageTab === 'reports' }"
          role="tab"
          :aria-selected="pageTab === 'reports'"
          @click="pageTab = 'reports'"
        >
          Reports
        </button>
        <button
          v-if="isAdmin && isAiCampaignForProgress"
          type="button"
          class="tw-topbar__tab"
          :class="{ 'is-active': pageTab === 'slotBooking' }"
          role="tab"
          :aria-selected="pageTab === 'slotBooking'"
          @click="pageTab = 'slotBooking'"
        >
          Slot booking
        </button>
        <button
          v-if="isAdmin && isAiCampaignForProgress"
          type="button"
          class="tw-topbar__tab"
          :class="{ 'is-active': pageTab === 'slotBookings' }"
          role="tab"
          :aria-selected="pageTab === 'slotBookings'"
          @click="pageTab = 'slotBookings'"
        >
          Bookings
        </button>
        <button
          v-if="isAdmin"
          type="button"
          class="tw-topbar__tab"
          :class="{ 'is-active': pageTab === 'settings' }"
          role="tab"
          :aria-selected="pageTab === 'settings'"
          @click="pageTab = 'settings'"
        >
          Settings
        </button>
      </div>

      <div v-if="hasAnyTopbarAction" class="tw-topbar__actions">
        <v-menu location="bottom end" offset="8" :close-on-content-click="true">
          <template #activator="{ props: menuProps }">
            <button
              v-bind="menuProps"
              type="button"
              class="tw-btn tw-btn--primary"
              :disabled="loading"
            >
              <v-icon icon="mdi-lightning-bolt-outline" size="16" />
              Actions
              <v-icon icon="mdi-chevron-down" size="16" />
            </button>
          </template>
          <div class="tk-actions-menu">
            <button
              v-if="canRunAiCalls"
              type="button"
              class="tk-actions-menu__item"
              :disabled="runningAiCalls || !canUpdateLeadStatuses"
              :title="leadStatusGate.reason || undefined"
              @click="confirmRunAiCalls"
            >
              <span class="tk-actions-menu__icon tk-actions-menu__icon--run">
                <v-icon icon="mdi-robot-outline" size="18" />
              </span>
              <span class="tk-actions-menu__text">
                <span class="tk-actions-menu__title">{{ runningAiCalls ? 'Starting…' : 'Run AI calls' }}</span>
                <span class="tk-actions-menu__desc">Start AI dialing for queued leads</span>
              </span>
            </button>
            <button
              v-if="canCreateLeadHere"
              type="button"
              class="tk-actions-menu__item"
              :disabled="loading || isCampaignProcessing"
              @click="showCreateLeadDialog = true"
            >
              <span class="tk-actions-menu__icon tk-actions-menu__icon--create">
                <v-icon icon="mdi-account-plus-outline" size="18" />
              </span>
              <span class="tk-actions-menu__text">
                <span class="tk-actions-menu__title">Create lead</span>
                <span class="tk-actions-menu__desc">Add a single lead to this campaign</span>
              </span>
            </button>
            <button
              v-if="canAssignLeads"
              type="button"
              class="tk-actions-menu__item"
              :disabled="loading || isCampaignProcessing || !filteredLeads.length"
              @click="showBulkAssignDialog = true"
            >
              <span class="tk-actions-menu__icon tk-actions-menu__icon--assign">
                <v-icon icon="mdi-account-multiple-plus-outline" size="18" />
              </span>
              <span class="tk-actions-menu__text">
                <span class="tk-actions-menu__title">Bulk assign</span>
                <span class="tk-actions-menu__desc">Assign many leads at once</span>
              </span>
            </button>
            <button
              v-if="canBulkUpdateStatus"
              type="button"
              class="tk-actions-menu__item"
              :disabled="loading || isCampaignProcessing || !leads.length"
              :title="isCampaignProcessing ? leadStatusGate.reason : undefined"
              @click="showBulkStatusDialog = true"
            >
              <span class="tk-actions-menu__icon tk-actions-menu__icon--status">
                <v-icon icon="mdi-playlist-edit" size="18" />
              </span>
              <span class="tk-actions-menu__text">
                <span class="tk-actions-menu__title">Bulk status</span>
                <span class="tk-actions-menu__desc">Update status for many leads at once</span>
              </span>
            </button>
            <button
              v-if="canEditCampaign"
              type="button"
              class="tk-actions-menu__item"
              :disabled="loading || isCampaignProcessing"
              @click="onEditClick"
            >
              <span class="tk-actions-menu__icon tk-actions-menu__icon--edit">
                <v-icon icon="mdi-pencil-outline" size="18" />
              </span>
              <span class="tk-actions-menu__text">
                <span class="tk-actions-menu__title">Edit</span>
                <span class="tk-actions-menu__desc">Change campaign settings</span>
              </span>
            </button>
            <!-- Dashboard and Call History tabs each have their own dedicated
                 Refresh (reloads just that tab's own data) -- showing this one
                 too meant two Refresh buttons doing different things stacked on
                 top of each other. Confirmed live on Call History 2026-08-20
                 (Ramu: "double refresh button ??"). -->
            <button
              v-if="showTopbarRefresh"
              type="button"
              class="tk-actions-menu__item"
              :disabled="loading"
              @click="refreshAll"
            >
              <span class="tk-actions-menu__icon tk-actions-menu__icon--refresh">
                <v-icon icon="mdi-refresh" size="18" />
              </span>
              <span class="tk-actions-menu__text">
                <span class="tk-actions-menu__title">Refresh</span>
                <span class="tk-actions-menu__desc">Reload this tab's data</span>
              </span>
            </button>
          </div>
        </v-menu>
      </div>
    </header>

    <div
      v-if="isCampaignProcessing"
      class="tw-processing-banner"
      role="status"
    >
      <v-progress-circular indeterminate size="18" width="2" />
      <div>
        <strong>Processing leads…</strong>
        <span>
          Lead selection is still running in the background. Existing leads stay
          in the campaign; newly matched leads will appear when it becomes Active.
        </span>
      </div>
    </div>

    <div
      v-else-if="campaignLifecycleBanner"
      class="tw-processing-banner"
      :class="`tw-processing-banner--${campaignLifecycleBanner.tone}`"
      role="status"
    >
      <v-icon :icon="campaignLifecycleBanner.icon" size="18" />
      <div>
        <strong>{{ campaignLifecycleBanner.title }}</strong>
        <span>{{ campaignLifecycleBanner.body }}</span>
      </div>
    </div>

    <!-- Queued / In progress / Done -- tallied client-side off the leads
         already fetched for the list right below it (see queuedLeadsCount
         and friends). AI campaigns only: see isAiCampaignForProgress.
         Deliberately NOT a Dashboard card -- see the git history on this
         page for why that placement was reverted (80b0859/6d1f20d/560f94e):
         forcing a 5th metric into the Dashboard's fixed 4-column grid never
         produced a coherent layout across several attempts. Sitting here
         instead costs no extra fetch (leads are already loaded for this
         tab) and sits directly above the very rows it summarizes. -->
    <div
      v-if="pageTab === 'leads' && isAiCampaignForProgress && leads.length"
      class="tw-progress-strip"
      :class="{ 'tw-progress-strip--calm': campaignFullyWorked }"
    >
      <template v-if="campaignFullyWorked">
        <v-icon icon="mdi-check-circle-outline" size="16" class="tw-progress-strip__calm-icon" />
        <span class="tw-progress-strip__calm-text">All caught up — nothing queued or ringing</span>
        <span class="tw-progress-strip__spacer" />
        <span class="tw-progress-strip__stat tw-progress-strip__stat--done">
          <strong>{{ doneLeadsCount }}</strong> done
        </span>
      </template>
      <template v-else>
        <span class="tw-progress-strip__stat tw-progress-strip__stat--queued">
          <strong>{{ queuedLeadsCount }}</strong> queued
        </span>
        <span class="tw-progress-strip__divider" aria-hidden="true" />
        <span
          class="tw-progress-strip__stat tw-progress-strip__stat--inprogress"
          :title="inProgressLeadsCount ? 'Calls ringing right now' : undefined"
        >
          <span v-if="inProgressLeadsCount" class="tw-progress-strip__pulse" aria-hidden="true" />
          <strong>{{ inProgressLeadsCount }}</strong> in progress
        </span>
        <span class="tw-progress-strip__divider" aria-hidden="true" />
        <span class="tw-progress-strip__stat tw-progress-strip__stat--done">
          <strong>{{ doneLeadsCount }}</strong> done
        </span>
        <span class="tw-progress-strip__spacer" />
        <span class="tw-progress-strip__total">
          {{ leads.length }} lead{{ leads.length === 1 ? '' : 's' }} total
        </span>
      </template>
    </div>

    <div v-show="pageTab === 'leads'" class="tw-shell">
      <!-- LEFT: Lead queue -->
      <aside class="tw-queue" aria-label="Lead queue">
        <div class="tw-queue__filters">
          <input
            v-model="search"
            class="tw-input tw-queue__search"
            type="search"
            placeholder="Search leads…"
          />
          <div class="tw-queue__filter-row">
            <select
              v-if="isAdmin"
              v-model="adminLeadScope"
              class="tw-select"
              title="Lead scope"
              @change="onAdminLeadScopeChange"
            >
              <option value="all">All leads</option>
              <option value="mine">My leads</option>
            </select>
            <select v-model="statusFilter" class="tw-select" :title="'Status'">
              <option
                v-for="opt in statusFilterOptions"
                :key="`st-${opt.value}`"
                :value="opt.value"
              >
                {{ opt.title }}
              </option>
            </select>
            <select
              v-if="isAdmin && adminLeadScope === 'all'"
              v-model="assigneeFilter"
              class="tw-select"
              :title="'Assigned to'"
            >
              <option
                v-for="opt in assigneeFilterOptions"
                :key="`as-${opt.value}`"
                :value="opt.value"
              >
                {{ opt.title }}
              </option>
            </select>
          </div>
          <div class="tw-queue__count">
            {{ filteredLeads.length }} lead{{ filteredLeads.length === 1 ? '' : 's' }}
          </div>
        </div>

        <div class="tw-queue__list">
          <template v-if="loading && !leads.length">
            <div
              v-for="n in 6"
              :key="n"
              class="tw-lead-card"
              style="opacity: 0.45; min-height: 4.5rem"
            />
          </template>

          <template v-else-if="filteredLeads.length">
            <button
              v-for="lead in filteredLeads"
              :key="lead.id"
              type="button"
              class="tw-lead-card"
              :class="{ 'is-selected': selectedId === lead.id }"
              @click="selectLead(lead)"
            >
              <div class="tw-avatar">{{ initials(displayLeadName(lead)) }}</div>
              <div class="tw-lead-card__body">
                <div class="tw-lead-card__top">
                  <span class="tw-lead-card__name">{{ displayLeadName(lead) }}</span>
                  <span class="tw-priority" :class="priorityClass(lead)" />
                </div>
                <div class="tw-lead-card__phone">
                  <span class="tw-lead-card__phone-text">
                    {{ lead.phone || lead.phoneRaw || 'No phone' }}
                  </span>
                  <span
                    v-if="leadDialNumber(lead)"
                    class="tw-lead-card__call"
                    role="button"
                    tabindex="0"
                    :title="`Call ${lead.phone || lead.phoneRaw}`"
                    :aria-label="`Call ${displayLeadName(lead)}`"
                    :aria-disabled="!canCallLead(lead) ? 'true' : 'false'"
                    @click.stop="onLeadListCall(lead)"
                    @keydown.enter.stop.prevent="onLeadListCall(lead)"
                  >
                    <v-icon icon="mdi-phone" size="13" />
                  </span>
                </div>
                <div class="tw-lead-card__foot">
                  <span class="tw-badge" :class="`tw-badge--${lead.statusTone || 'neutral'}`">
                    {{ lead.statusLabel }}
                  </span>
                  <span v-if="leadMetaLine(lead)" class="tw-lead-card__meta">
                    {{ leadMetaLine(lead) }}
                  </span>
                </div>
              </div>
            </button>
          </template>

          <div v-else class="tw-empty">
            <v-icon
              :icon="isCampaignProcessing ? 'mdi-progress-clock' : 'mdi-account-search-outline'"
              size="36"
            />
            <p class="tw-empty__title">
              {{ isCampaignProcessing ? 'Processing leads…' : 'No matching leads' }}
            </p>
            <p class="tw-empty__sub">
              {{
                isCampaignProcessing
                  ? 'Assignments are still running. This list will update when the campaign becomes Active.'
                  : 'Try another search or clear filters.'
              }}
            </p>
          </div>
        </div>
      </aside>

      <!-- RIGHT: lead panel (tabs switch content; queue stays fixed) -->
      <main class="tw-workspace" aria-label="Lead workspace">
        <div class="tw-panel-tabs" role="tablist" aria-label="Lead views">
          <button
            type="button"
            class="tw-panel-tabs__tab"
            :class="{ 'is-active': leadTab === 'details' }"
            role="tab"
            :aria-selected="leadTab === 'details'"
            @click="leadTab = 'details'"
          >
            Details
          </button>
          <button
            type="button"
            class="tw-panel-tabs__tab"
            :class="{ 'is-active': leadTab === 'history' }"
            role="tab"
            :aria-selected="leadTab === 'history'"
            @click="leadTab = 'history'"
          >
            History
          </button>
        </div>

        <div class="tw-panel-body">
          <div v-show="leadTab === 'details'" class="tw-panel-body__details">
        <template v-if="selected">
          <div class="tw-workspace__pin">
            <div class="tw-hero">
              <div class="tw-avatar tw-avatar--lg">{{ initials(displayLeadName(selected)) }}</div>
              <div class="tw-hero__copy">
                <h2 class="tw-hero__name">{{ displayLeadName(selected) }}</h2>
                <div class="tw-hero__row">
                  <span><strong>Phone</strong> {{ selected.phone || selected.phoneRaw || '—' }}</span>
                  <span><strong>Email</strong> {{ selected.email || '—' }}</span>
                  <span v-if="selected.company"><strong>Company</strong> {{ selected.company }}</span>
                </div>
                <div class="tw-hero__badges">
                  <span class="tw-badge" :class="`tw-badge--${selected.statusTone || 'neutral'}`">
                    {{ selected.statusLabel }}
                  </span>
                  <span v-if="selected.contextName" class="tw-badge tw-badge--info">
                    {{ selected.contextName }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="tw-workspace__scroll">
            <div v-if="canAssignLeads" class="tw-actions">
              <button
                type="button"
                class="tw-btn tw-btn--ghost"
                @click="openSections.assign = !openSections.assign"
              >
                <v-icon icon="mdi-account-arrow-right" size="16" />
                Assign
              </button>
            </div>

            <div v-if="openSections.assign && canAssignLeads" class="tw-section">
              <div class="tw-section__body" style="padding-top: 0.85rem">
                <div class="tw-form-grid">
                  <label>
                    Assign to agent
                    <select
                      class="tw-select"
                      :value="selected.assignedAgentId || ''"
                      @change="onAssignSelect($event)"
                    >
                      <option value="" disabled>Select agent</option>
                      <option
                        v-for="agent in assignableAgents"
                        :key="agent.id"
                        :value="agent.id"
                      >
                        {{ agent.name || agent.email || agent.id }}
                      </option>
                    </select>
                  </label>
                </div>
              </div>
            </div>

            <!-- Customer Information -->
            <section class="tw-section">
              <div class="tw-section__head tw-section__head--actions">
                <button type="button" class="tw-section__head-btn" @click="toggleSection('info')">
                  <span class="tw-section__title">Customer Information</span>
                  <v-icon
                    :icon="openSections.info ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                    size="18"
                  />
                </button>
                <div v-if="openSections.info" class="tw-section__actions">
                  <template v-if="!editingWorkspaceFields">
                    <button
                      type="button"
                      class="tw-btn tw-btn--ghost"
                      :disabled="!canEditSelectedLead"
                      :title="canEditSelectedLead ? undefined : editSelectedLeadBlockedReason"
                      @click="startWorkspaceFieldEdit"
                    >
                      <v-icon icon="mdi-pencil-outline" size="16" />
                      Edit fields
                    </button>
                  </template>
                  <template v-else>
                    <button
                      type="button"
                      class="tw-btn tw-btn--ghost"
                      :disabled="savingWorkspaceFields"
                      @click="cancelWorkspaceFieldEdit"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      class="tw-btn tw-btn--primary"
                      :disabled="savingWorkspaceFields"
                      @click="saveWorkspaceFields"
                    >
                      {{ savingWorkspaceFields ? 'Saving…' : 'Save fields' }}
                    </button>
                  </template>
                </div>
              </div>
              <div v-if="openSections.info" class="tw-section__body">
                <div v-if="!editingWorkspaceFields" class="tw-info-grid">
                  <div
                    v-for="field in infoFields"
                    :key="field.key"
                    class="tw-info-card"
                    :class="{ 'tw-info-card--wide': field.long }"
                  >
                    <span class="tw-info-card__label">{{ field.label }}</span>
                    <div class="tw-info-card__value-row">
                      <span class="tw-info-card__value" :title="field.value">
                        {{ field.value || '—' }}
                      </span>
                      <button
                        v-if="field.callable && field.dialValue"
                        type="button"
                        class="tw-info-card__call"
                        :disabled="!canCallLead(selected)"
                        :title="`Call ${field.value}`"
                        :aria-label="`Call ${field.label}`"
                        @click.stop="openCallDialog(selected, field.dialValue)"
                      >
                        <v-icon icon="mdi-phone" size="14" />
                      </button>
                    </div>
                  </div>
                  <p
                    v-if="!canEditSelectedLead"
                    class="tw-insight__muted tw-insight__muted--tight"
                    style="margin-top: 0.55rem"
                  >
                    {{ editSelectedLeadBlockedReason }}
                  </p>
                </div>
                <div v-else class="tw-form-grid">
                  <label
                    v-for="field in workspaceFieldDefs"
                    :key="`ws-${field.key}`"
                    :class="{ 'tw-form-grid__full': field.long }"
                  >
                    {{ field.label }}
                    <select
                      v-if="isDropdownField(field)"
                      v-model="workspaceFields[field.key]"
                      class="tw-select"
                      :disabled="field.locked"
                    >
                      <option value="">Select…</option>
                      <option
                        v-for="opt in field.options"
                        :key="`ws-${field.key}-${opt}`"
                        :value="opt"
                      >
                        {{ opt }}
                      </option>
                      <option
                        v-if="!(field.options && field.options.length)"
                        value=""
                        disabled
                      >
                        No options configured
                      </option>
                    </select>
                    <textarea
                      v-else-if="field.long"
                      v-model="workspaceFields[field.key]"
                      class="tw-textarea"
                      rows="2"
                      :placeholder="field.placeholder || ''"
                      :disabled="field.locked"
                    />
                    <input
                      v-else-if="field.type === 'DATE'"
                      v-model="workspaceFields[field.key]"
                      class="tw-input"
                      type="date"
                      :disabled="field.locked"
                    />
                    <input
                      v-else-if="field.type === 'TIME'"
                      v-model="workspaceFields[field.key]"
                      class="tw-input"
                      type="time"
                      :disabled="field.locked"
                    />
                    <input
                      v-else
                      v-model="workspaceFields[field.key]"
                      class="tw-input"
                      :type="
                        field.type === 'NUMBER'
                          ? 'number'
                          : field.type === 'PHONE_NUMBER'
                            ? 'tel'
                            : field.type === 'EMAIL'
                              ? 'email'
                              : 'text'
                      "
                      :placeholder="field.placeholder || ''"
                      :disabled="field.locked"
                    />
                  </label>
                </div>
              </div>
            </section>

            <!-- Status update: request_status + comments (API-required, not product fields) -->
            <section class="tw-section">
              <div class="tw-section__head tw-section__head--actions">
                <button type="button" class="tw-section__head-btn" @click="toggleSection('status')">
                  <span class="tw-section__title">Update status</span>
                  <v-icon
                    :icon="openSections.status ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                    size="18"
                  />
                </button>
              </div>
              <div v-if="openSections.status" class="tw-section__body">
                <div class="tw-form-grid">
                  <label>
                    <span class="tw-field-label">
                      Status <span class="tw-required">*</span>
                    </span>
                    <select v-model="workspaceStatus" class="tw-select">
                      <option value="" disabled>Select status</option>
                      <option
                        v-for="opt in workspaceStatusOptions"
                        :key="`ws-st-${opt.value}`"
                        :value="opt.value"
                      >
                        {{ opt.title }}
                      </option>
                    </select>
                  </label>
                  <label class="tw-form-grid__full">
                    <span class="tw-field-label">
                      Comments <span class="tw-required">*</span>
                    </span>
                    <textarea
                      v-model="workspaceStatusComments"
                      class="tw-textarea"
                      rows="3"
                      placeholder="Note explaining this status change…"
                    />
                  </label>
                  <label>
                    <span class="tw-field-label">
                      Call back on
                      <span style="font-weight: 400; opacity: 0.65">optional</span>
                    </span>
                    <input
                      v-model="workspaceCallbackDate"
                      type="date"
                      class="tw-select"
                      :min="tomorrowDateInputValue"
                    />
                  </label>
                  <label v-if="workspaceCallbackDate">
                    <span class="tw-field-label">Callback note</span>
                    <input
                      v-model="workspaceCallbackNote"
                      type="text"
                      class="tw-select"
                      placeholder="What to call back about…"
                    />
                  </label>
                </div>
                <div class="tw-actions" style="margin-top: 0.75rem; margin-bottom: 0">
                  <button
                    type="button"
                    class="tw-btn tw-btn--primary"
                    :disabled="
                      savingWorkspaceStatus ||
                      !canUpdateLeadStatuses ||
                      !workspaceStatus ||
                      !workspaceStatusComments.trim()
                    "
                    :title="leadStatusGate.reason || undefined"
                    @click="saveWorkspaceStatus"
                  >
                    {{ savingWorkspaceStatus ? 'Updating…' : 'Update status' }}
                  </button>
                </div>
                <p
                  v-if="!canUpdateLeadStatuses && leadStatusGate.reason"
                  class="tw-insight__muted tw-insight__muted--tight"
                  style="margin-top: 0.55rem"
                >
                  {{ leadStatusGate.reason }}
                </p>
              </div>
            </section>
          </div>
        </template>

        <div v-else class="tw-placeholder">
          <div>
            <v-icon icon="mdi-card-account-details-outline" size="48" color="#4F46E5" />
            <p class="tw-empty__title">Select a lead to open the workspace</p>
            <p class="tw-empty__sub">Browse the queue on the left to get started.</p>
          </div>
        </div>
          </div>

          <div v-show="leadTab === 'history'" class="tw-history-pane">
      <div v-if="!selected" class="tw-history-empty">
        <div class="tw-call__art">
          <v-icon icon="mdi-history" size="40" />
        </div>
        <p class="tw-empty__title">Select a lead</p>
        <p class="tw-empty__sub">
          Pick a lead from the list to review status, comments, and recordings.
        </p>
      </div>

      <template v-else>
        <header class="tw-history-head">
          <div class="tw-history-head__lead">
            <div class="tw-avatar tw-avatar--lg">
              {{ initials(displayLeadName(selected)) }}
            </div>
            <div class="tw-history-head__copy">
              <p class="tw-history-head__kicker">Lead history</p>
              <h2 class="tw-history-head__name">{{ displayLeadName(selected) }}</h2>
              <p class="tw-history-head__meta">
                {{ selected.phone || selected.phoneRaw || 'No phone' }}
                <span v-if="selected.statusLabel">· {{ selected.statusLabel }}</span>
              </p>
            </div>
          </div>
          <div class="tw-history-head__actions">
            <button
              v-if="canCallLead(selected)"
              type="button"
              class="tw-btn tw-btn--primary"
              @click="openCallDialog(selected)"
            >
              <v-icon icon="mdi-phone" size="16" />
              Call
            </button>
          </div>
        </header>

        <div class="tw-history-layout">
          <aside class="tw-history-summary" aria-label="Lead score summary">
            <section
              class="tw-insight__hero"
              :class="`tw-insight__hero--${selectedAiMetrics.scoreTone}`"
            >
              <div class="tw-insight__hero-top">
                <div>
                  <p class="tw-insight__kicker">Interest score</p>
                  <p class="tw-insight__hero-caption">
                    AI read of this lead’s calls
                  </p>
                </div>
              </div>
              <div class="tw-insight__gauge">
                <div
                  class="tw-insight__ring"
                  :style="selectedAiMetrics.scoreRingStyle"
                  role="img"
                  :aria-label="`Lead score ${selectedAiMetrics.scoreLabel} of 100`"
                >
                  <div class="tw-insight__ring-core">
                    <span class="tw-insight__ring-num">
                      {{ selectedAiMetrics.scoreLabel }}
                    </span>
                    <span
                      v-if="selectedAiMetrics.score != null"
                      class="tw-insight__ring-max"
                    >/100</span>
                  </div>
                </div>
                <ul class="tw-insight__chips" aria-label="Call summary">
                  <li>
                    <span>{{ selectedAiMetrics.totalCalls }}</span>
                    <small>{{ selectedAiMetrics.totalCalls === 1 ? 'call' : 'calls' }}</small>
                  </li>
                  <li>
                    <span>{{ selectedAiMetrics.talkTimeLabel }}</span>
                    <small>talk</small>
                  </li>
                  <li>
                    <span>{{ selectedAiMetrics.lastCallStatusLabel }}</span>
                    <small>last</small>
                  </li>
                </ul>
              </div>
            </section>

            <section class="tw-history-summary__card">
              <h3 class="tw-insight__label">
                <v-icon icon="mdi-text-box-outline" size="14" />
                Overview
              </h3>
              <blockquote
                class="tw-insight__quote"
                :class="{ 'is-empty': !selectedAiMetrics.overview }"
              >
                {{
                  selectedAiMetrics.overview ||
                  'Overview appears after a scored call or a status update with comments.'
                }}
              </blockquote>
            </section>

            <section
              v-if="selectedBehaviorProfile"
              class="tw-history-summary__card"
            >
              <h3 class="tw-insight__label">
                <v-icon icon="mdi-account-voice" size="14" />
                Caller profile
              </h3>
              <blockquote class="tw-insight__quote">
                {{ selectedBehaviorProfile }}
              </blockquote>
              <p class="tw-insight__hero-caption">
                Built from every call this org has had with this number,
                across all campaigns.
              </p>
            </section>

            <section class="tw-history-summary__card">
              <div
                class="tw-insight__best"
                :class="{ 'is-empty': !selectedAiMetrics.bestTimeLabel }"
              >
                <span class="tw-insight__best-icon" aria-hidden="true">
                  <v-icon icon="mdi-clock-outline" size="18" />
                </span>
                <div class="tw-insight__best-copy">
                  <h3 class="tw-insight__label tw-insight__label--inline">
                    Best time to call
                  </h3>
                  <p>
                    {{
                      selectedAiMetrics.bestTimeLabel ||
                      'Not captured yet'
                    }}
                  </p>
                </div>
              </div>
            </section>

            <section
              v-if="selectedInternalFields.length"
              class="tw-history-summary__card"
            >
              <div
                v-for="field in selectedInternalFields"
                :key="field.key"
                class="tw-insight__best"
                :class="{ 'is-empty': !field.display }"
              >
                <span class="tw-insight__best-icon" aria-hidden="true">
                  <v-icon icon="mdi-calendar-clock-outline" size="18" />
                </span>
                <div class="tw-insight__best-copy">
                  <h3 class="tw-insight__label tw-insight__label--inline">
                    {{ field.label }}
                  </h3>
                  <p>{{ field.display || 'Not set' }}</p>
                </div>
              </div>
            </section>
          </aside>

          <section class="tw-history-feed" aria-label="Activity history">
            <div class="tw-history-feed__head">
              <h3 class="tw-history-feed__title">Activity</h3>
              <span
                v-if="selectedActivityFeed.length"
                class="tw-insight__count"
              >
                {{ selectedActivityFeed.length }}
              </span>
              <v-btn
                icon="mdi-refresh"
                size="small"
                variant="text"
                density="comfortable"
                :loading="selectedHistoryLoading"
                title="Refresh activity"
                class="tw-history-feed__refresh"
                @click="loadSelectedLeadHistory"
              />
            </div>

            <p v-if="selectedHistoryLoading" class="tw-insight__muted">
              Loading history…
            </p>
            <p
              v-else-if="!selectedActivityFeed.length"
              class="tw-insight__muted"
            >
              No status updates or calls for this lead yet.
            </p>
            <ol v-else class="tw-history-feed__list">
              <li
                v-for="entry in selectedActivityFeed"
                :key="entry.id"
                class="tw-history-card"
                :class="`tw-history-card--${entry.kind}`"
              >
                <div class="tw-history-card__top">
                  <div>
                    <time class="tw-history-card__time">{{ entry.atLabel }}</time>
                    <p class="tw-history-card__kind">
                      <template v-if="entry.kind === 'call'">
                        Call
                        <template v-if="entry.callStatusLabel">
                          · {{ formatCallStatusLabel(entry.callStatusLabel) }}
                        </template>
                        <template v-if="entry.durationLabel">
                          · {{ entry.durationLabel }}
                        </template>
                      </template>
                      <template v-else>
                        Status update
                      </template>
                      <template v-if="entry.actorLabel">
                        · {{ entry.actorLabel }}
                      </template>
                    </p>
                  </div>
                  <div v-if="entry.requestStatus" class="tw-history-card__tags">
                    <span class="tw-history-card__status-label">Lead status</span>
                    <span
                      class="tw-badge"
                      :class="`tw-badge--${entry.requestStatusTone || 'neutral'}`"
                    >
                      {{ entry.requestStatusLabel }}
                    </span>
                    <v-btn
                      v-if="entry.performedBy === 'AI_AGENT' && canUpdateLeadStatuses"
                      size="small"
                      variant="tonal"
                      density="comfortable"
                      prepend-icon="mdi-pencil-outline"
                      class="tw-history-card__edit-btn"
                      title="This was written by the AI -- edit and resubmit below"
                      @click="editAiHistoryEntry(entry)"
                    >
                      Edit
                    </v-btn>
                  </div>
                </div>

                <div v-if="entry.comments" class="tw-history-card__comments">
                  <span class="tw-history-card__label">Comments</span>
                  <p>{{ entry.comments }}</p>
                </div>
                <p
                  v-else-if="entry.kind === 'status'"
                  class="tw-insight__muted tw-insight__muted--tight"
                >
                  No comments
                </p>

                <div v-if="entry.callbackRequestedOn" class="tw-history-card__callback">
                  <v-icon icon="mdi-phone-clock-outline" size="14" />
                  <span>Callback requested for {{ entry.callbackRequestedOnLabel }}</span>
                  <span v-if="entry.callbackNote">· {{ entry.callbackNote }}</span>
                </div>

                <div
                  v-if="entry.recordingUrl || entry.hasRecording || entry.recordingSource"
                  class="tw-history-card__recording"
                >
                  <span class="tw-history-card__label">
                    <v-icon icon="mdi-microphone" size="14" />
                    Recording
                  </span>
                  <audio
                    v-if="entry.recordingUrl"
                    controls
                    preload="none"
                    :src="entry.recordingUrl"
                    class="tw-history-card__audio"
                  >
                    Your browser does not support audio playback.
                  </audio>
                  <CallRecordingPlayer v-else :source="entry.recordingSource" />
                </div>

                <details
                  v-if="entry.transcript && entry.transcript.length"
                  class="tw-insight__transcript"
                >
                  <summary>
                    <v-icon icon="mdi-text" size="14" />
                    Transcript
                    <span>({{ entry.transcript.length }})</span>
                  </summary>
                  <ul>
                    <li
                      v-for="(line, idx) in entry.transcript"
                      :key="idx"
                    >
                      {{ line }}
                    </li>
                  </ul>
                </details>
              </li>
            </ol>
          </section>
        </div>
      </template>
    </div>

        </div>
      </main>
    </div>

    <div v-show="pageTab === 'dashboard'" class="tw-dashboard-pane tw-dashboard-pane--page">
      <CampaignDashboardPanel
        :campaign-id="campaignId"
        :campaign-name="campaign?.name || ''"
        :converted-status="metricsConvertedStatus"
        :active="pageTab === 'dashboard'"
        :leads="leads"
        :is-admin="isAdmin"
        show-header
      />
    </div>

    <div v-show="pageTab === 'callHistory'" class="tw-reports-pane">
      <!-- No repeated eyebrow/title header here -- the campaign name is
           already shown once in the page's own top bar just above the tabs,
           and this tab's toolbar (inside the panel below) already gives the
           call count and its own Refresh. Repeating name+description again
           here was pure vertical space with nothing new in it. Confirmed
           live 2026-08-20 (Ramu: "Lot of space on top and duplicate level"). -->
      <p class="tw-reports-pane__sub tw-reports-pane__sub--standalone">
        Every call this campaign originally received, plus every call redirected here from elsewhere.
      </p>
      <CampaignCallHistoryPanel
        :campaign-id="campaignId"
        @error="(msg) => showSnack('error', msg)"
      />
    </div>

    <div v-show="pageTab === 'dialAttempts'" class="tw-reports-pane">
      <!-- Same "no repeated header" reasoning as the Call History tab above
           -- the panel's own toolbar already gives the attempt count and its
           own Refresh, and the summary strip inside it is the actual
           headline for this tab. -->
      <p class="tw-reports-pane__sub tw-reports-pane__sub--standalone">
        Every dial this campaign made -- connected or not -- most recent first.
      </p>
      <CampaignDialAttemptsPanel
        :campaign-id="campaignId"
        @error="(msg) => showSnack('error', msg)"
      />
    </div>

    <div v-show="pageTab === 'reports'" class="tw-reports-pane">
      <header class="tw-reports-pane__head">
        <div>
          <p class="tw-reports-pane__eyebrow">Campaign reports</p>
          <h2 class="tw-reports-pane__title">
            {{ campaign?.name || 'Reports' }}
          </h2>
          <p class="tw-reports-pane__sub">
            Export-ready summaries and performance reports for this campaign.
          </p>
        </div>
      </header>
      <CampaignReportsPanel
        :campaign-id="campaignId"
        :campaign-name="campaign?.name || ''"
        :converted-status="metricsConvertedStatus"
      />
      <ReportDigestSettings
        v-if="isAdmin"
        :campaign-id="campaignId"
        :model-value="campaign?.reportDigestConfig || {}"
        @saved="(msg) => showSnack('success', msg)"
        @error="(msg) => showSnack('error', msg)"
      />
    </div>

    <div v-show="isAdmin && pageTab === 'settings'" class="tw-reports-pane">
      <header class="tw-reports-pane__head tw-settings-pane__head">
        <div>
          <p class="tw-reports-pane__eyebrow">Campaign settings</p>
          <h2 class="tw-reports-pane__title">
            {{ campaign?.name || 'Settings' }}
          </h2>
          <p class="tw-reports-pane__sub">
            Everything beyond the basics -- lead filters, the call script, transfer routing,
            auto-dial, lead-adoption behavior, and reporting digests.
          </p>
        </div>
      </header>
      <div class="tw-settings-pane__body">
        <CampaignAdvancedSettings
          :campaign="campaign"
          :contexts="campaignContexts"
          :all-contexts="contexts"
          :agents="agents"
          :disabled="!canEditCampaign"
          @saved="(msg) => onCampaignSettingsSaved(msg)"
          @error="(msg) => showSnack('error', msg)"
        />

        <v-divider style="margin: 1.75rem 0" />

        <header class="tw-settings-pane__head">
          <div>
            <p class="tw-reports-pane__eyebrow">Reporting digest</p>
            <p class="tw-reports-pane__sub">
              Periodically email/WhatsApp this campaign's KPIs and agent activity, instead of
              relying on someone to come check the dashboard.
            </p>
          </div>
          <button
            type="button"
            class="tw-btn tw-btn--primary"
            style="margin-left: auto; flex-shrink: 0"
            :disabled="savingDigest"
            @click="saveDigestSettings"
          >
            <v-icon icon="mdi-content-save-outline" size="16" />
            {{ savingDigest ? 'Saving…' : 'Save' }}
          </button>
        </header>
        <DashboardDigestSettings
          v-model="digestSettings"
          v-model:send-now="digestSendNow"
          scope-label="campaign"
          :disabled="savingDigest"
        />
      </div>
    </div>

    <div v-show="isAdmin && isAiCampaignForProgress && pageTab === 'slotBooking'" class="tw-reports-pane">
      <CampaignSlotBooking
        :campaign="campaign"
        :disabled="!canEditCampaign"
        @saved="(msg) => showSnack('success', msg)"
        @error="(msg) => showSnack('error', msg)"
      />
    </div>

    <div v-show="isAdmin && isAiCampaignForProgress && pageTab === 'slotBookings'" class="tw-reports-pane">
      <CampaignSlotBookings
        :campaign="campaign"
        :disabled="!canEditCampaign"
        @saved="(msg) => showSnack('success', msg)"
        @error="(msg) => showSnack('error', msg)"
      />
    </div>

    <!-- Call workspace dialog — teleported to body so parent overflow:hidden can't clip it -->
    <Teleport to="body">
      <div
        v-if="showCallDialog"
        class="tw-call-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Start call"
      >
        <button
          type="button"
          class="tw-call-modal__backdrop"
          aria-label="Close dialog"
          @click="closeCallDialog"
        />
        <div class="tw-call-modal__panel" @click.stop="callStatusMenuOpen = false">
          <header class="tw-call-modal__head">
            <div>
              <h2 class="tw-call-modal__title">Start call</h2>
              <p class="tw-call-modal__sub">
                Update status and lead fields, add comments, then place the call
              </p>
            </div>
            <button type="button" class="tw-icon-btn" aria-label="Close" @click="closeCallDialog">
              <v-icon icon="mdi-close" size="18" />
            </button>
          </header>

          <div class="tw-call-modal__grid">
            <!-- Left: lead info + status + comments -->
            <section class="tw-call-modal__lead">
              <div class="tw-call-modal__lead-hero">
                <div class="tw-avatar tw-avatar--lg">
                  {{ initials(displayLeadName(callDialogLead)) }}
                </div>
                <div>
                  <h3 class="tw-hero__name">{{ displayLeadName(callDialogLead) }}</h3>
                  <p class="tw-lead-card__phone">
                    {{ callDialogLead?.phone || callDialogLead?.phoneRaw || '—' }}
                  </p>
                  <div class="tw-hero__badges" style="margin-top: 0.5rem">
                    <span
                      class="tw-badge"
                      :class="`tw-badge--${callDialogLead?.statusTone || 'neutral'}`"
                    >
                      {{ callDialogLead?.statusLabel || '—' }}
                    </span>
                    <span v-if="callDialogLead?.contextName" class="tw-badge tw-badge--info">
                      {{ callDialogLead.contextName }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="tw-call-modal__form">
                <label class="tw-call-modal__status-field">
                  <span class="tw-field-label">
                    Status <span class="tw-required">*</span>
                  </span>
                  <div
                    class="tw-status-menu"
                    :class="{ 'is-open': callStatusMenuOpen }"
                    @click.stop
                  >
                    <button
                      type="button"
                      class="tw-status-menu__trigger"
                      :aria-expanded="callStatusMenuOpen ? 'true' : 'false'"
                      aria-haspopup="listbox"
                      @click.stop="callStatusMenuOpen = !callStatusMenuOpen"
                    >
                      <span>
                        {{
                          callDialogStatusLabel || 'Select status'
                        }}
                      </span>
                      <v-icon
                        :icon="
                          callStatusMenuOpen
                            ? 'mdi-chevron-up'
                            : 'mdi-chevron-down'
                        "
                        size="18"
                      />
                    </button>
                    <ul
                      v-if="callStatusMenuOpen"
                      class="tw-status-menu__list"
                      role="listbox"
                    >
                      <li
                        v-for="opt in callDialogStatusOptions"
                        :key="opt.value"
                        role="option"
                        :aria-selected="callDialogStatus === opt.value"
                      >
                        <button
                          type="button"
                          class="tw-status-menu__option"
                          :class="{
                            'is-active': callDialogStatus === opt.value,
                          }"
                          @click.stop="selectCallDialogStatus(opt.value)"
                        >
                          {{ opt.title }}
                        </button>
                      </li>
                      <li
                        v-if="!callDialogStatusOptions.length"
                        class="tw-status-menu__empty"
                      >
                        No statuses configured for this product
                      </li>
                    </ul>
                  </div>
                </label>

                <div v-if="callDialogFieldDefs.length" class="tw-call-modal__fields">
                  <p class="tw-section__title" style="margin: 0 0 0.45rem">Update lead fields</p>
                  <div class="tw-form-grid">
                    <label
                      v-for="field in callDialogFieldDefs"
                      :key="field.key"
                      :class="{ 'tw-form-grid__full': field.long }"
                    >
                      {{ field.label }}
                      <select
                        v-if="isDropdownField(field)"
                        v-model="callDialogFields[field.key]"
                        class="tw-select"
                        :disabled="field.locked"
                      >
                        <option value="">Select…</option>
                        <option
                          v-for="opt in field.options"
                          :key="`${field.key}-${opt}`"
                          :value="opt"
                        >
                          {{ opt }}
                        </option>
                        <option
                          v-if="!(field.options && field.options.length)"
                          value=""
                          disabled
                        >
                          No options configured
                        </option>
                      </select>
                      <textarea
                        v-else-if="field.long"
                        v-model="callDialogFields[field.key]"
                        class="tw-textarea"
                        rows="2"
                        :placeholder="field.placeholder || ''"
                        :disabled="field.locked"
                      />
                      <input
                        v-else-if="field.type === 'DATE'"
                        v-model="callDialogFields[field.key]"
                        class="tw-input"
                        type="date"
                        :disabled="field.locked"
                      />
                      <input
                        v-else-if="field.type === 'TIME'"
                        v-model="callDialogFields[field.key]"
                        class="tw-input"
                        type="time"
                        :disabled="field.locked"
                      />
                      <input
                        v-else
                        v-model="callDialogFields[field.key]"
                        class="tw-input"
                        :type="
                          field.type === 'NUMBER'
                            ? 'number'
                            : field.type === 'PHONE_NUMBER'
                              ? 'tel'
                              : field.type === 'EMAIL'
                                ? 'email'
                                : 'text'
                        "
                        :placeholder="field.placeholder || ''"
                        :disabled="field.locked"
                      />
                    </label>
                  </div>
                </div>

                <label>
                  <span class="tw-field-label">
                    Comments <span class="tw-required">*</span>
                  </span>
                  <div
                    v-if="aiSuggestLoading || aiSuggestMessage || aiSuggestAvailable"
                    class="tw-ai-suggest"
                  >
                    <p v-if="aiSuggestLoading" class="tw-ai-suggest__status">
                      Generating AI suggestions from the call transcript…
                    </p>
                    <template v-else-if="aiSuggestAvailable">
                      <p class="tw-ai-suggest__status">
                        AI suggestions ready — review and edit before updating.
                      </p>
                      <button
                        type="button"
                        class="tw-btn tw-btn--ghost"
                        :disabled="savingCallStatus"
                        @click="applyAiSuggestions"
                      >
                        Apply suggestions
                      </button>
                    </template>
                    <p v-else-if="aiSuggestMessage" class="tw-ai-suggest__status">
                      {{ aiSuggestMessage }}
                    </p>
                  </div>
                  <textarea
                    v-model="callDialogComments"
                    class="tw-textarea"
                    rows="3"
                    placeholder="Required note for this status change…"
                  />
                </label>
                <button
                  type="button"
                  class="tw-btn tw-btn--primary"
                  :disabled="savingCallStatus || !callDialogStatus || !callDialogComments.trim()"
                  @click="saveCallDialogStatus"
                >
                  {{ savingCallStatus ? 'Updating…' : 'Update status' }}
                </button>
              </div>
            </section>

            <!-- Right: dialer -->
            <section class="tw-call-modal__dialer">
              <p class="tw-section__title" style="margin: 0 0 0.65rem">Dialer</p>

              <div
                v-if="callActive"
                class="tw-call-modal__live"
                :class="{
                  'is-ringing': callPhase === 'ringing',
                  'is-connected': callPhase === 'connected',
                }"
              >
                <div class="tw-ring-stage" aria-hidden="true">
                  <span class="tw-ring-stage__pulse" />
                  <span class="tw-ring-stage__pulse tw-ring-stage__pulse--delay" />
                  <div class="tw-ring-stage__icon">
                    <v-icon
                      :icon="callPhase === 'ringing' ? 'mdi-phone-ring' : 'mdi-phone-in-talk'"
                      size="28"
                    />
                  </div>
                </div>
                <div class="tw-call__timer">{{ formatDuration(callDurationSec) }}</div>
                <div class="tw-call__status">
                  {{ callPhase === 'ringing' ? 'Ringing…' : 'Connected' }}
                </div>
                <p class="tw-call-modal__live-name">
                  {{ callDialogLead?.name || 'Customer' }}
                </p>
                <div v-if="callPhase === 'connected'" class="tw-wave" aria-hidden="true">
                  <span /><span /><span /><span /><span /><span /><span />
                </div>
              </div>

              <input
                v-model="dialNumber"
                class="tw-input tw-call-modal__number"
                type="tel"
                inputmode="tel"
                :readonly="callActive"
                placeholder="Phone number"
              />

              <div class="tw-keypad tw-call-modal__pad">
                <button
                  v-for="digit in keypadDigits"
                  :key="`dial-${digit}`"
                  type="button"
                  @click="pressDialKey(digit)"
                >
                  {{ digit }}
                </button>
              </div>

              <div class="tw-call-modal__dial-actions">
                <button
                  v-if="!callActive"
                  type="button"
                  class="tw-call-modal__sip-btn"
                  :disabled="!canPlaceSipCall || placingSipCall"
                  @click.prevent.stop="placeSipCall"
                >
                  <v-progress-circular
                    v-if="placingSipCall"
                    indeterminate
                    size="18"
                    width="2"
                    color="white"
                  />
                  <v-icon v-else icon="mdi-phone" size="20" />
                  {{ placingSipCall ? 'Calling…' : 'Call' }}
                </button>
                <template v-else>
                  <button
                    type="button"
                    class="tw-call__ctrl"
                    :class="{ 'is-active': callMuted }"
                    @click="toggleMute"
                  >
                    <v-icon :icon="callMuted ? 'mdi-microphone-off' : 'mdi-microphone'" size="18" />
                    Mute
                  </button>
                  <button
                    type="button"
                    class="tw-call-modal__hangup-btn"
                    @click="endLiveCall"
                  >
                    <v-icon icon="mdi-phone-hangup" size="18" />
                    End call
                  </button>
                </template>
              </div>

              <p class="tw-call-modal__reg">
                Softphone:
                <strong :class="softphoneReady ? 'is-ready' : 'is-off'">
                  {{ softphoneReady ? 'Ready' : 'Not ready' }}
                </strong>
              </p>

              <p v-if="dialError" class="tw-call-modal__error">{{ dialError }}</p>
              <p v-else-if="placingSipCall" class="tw-call-modal__hint">
                Starting call — dialing from your softphone…
              </p>
              <p v-else class="tw-call-modal__hint">
                Confirm the number, then press Call.
              </p>
            </section>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import CreateCampaignDialog from '@/components/Campaigns/CreateCampaignDialog.vue'
import BulkAssignLeadsDialog from '@/components/Campaigns/BulkAssignLeadsDialog.vue'
import BulkUpdateCampaignLeadStatusDialog from '@/components/Campaigns/BulkUpdateCampaignLeadStatusDialog.vue'
import RunAiCallsResultDialog from '@/components/Campaigns/RunAiCallsResultDialog.vue'
import CallRecordingPlayer from '@/components/CallHistory/CallRecordingPlayer.vue'
import CampaignDashboardPanel from '@/components/Campaigns/CampaignDashboardPanel.vue'
import CampaignReportsPanel from '@/components/Campaigns/CampaignReportsPanel.vue'
import CampaignCallHistoryPanel from '@/components/Campaigns/CampaignCallHistoryPanel.vue'
import CampaignDialAttemptsPanel from '@/components/Campaigns/CampaignDialAttemptsPanel.vue'
import ReportDigestSettings from '@/components/Campaigns/ReportDigestSettings.vue'
import CampaignAdvancedSettings from '@/components/Campaigns/CampaignAdvancedSettings.vue'
import CampaignSlotBooking from '@/components/Campaigns/CampaignSlotBooking.vue'
import CampaignSlotBookings from '@/components/Campaigns/CampaignSlotBookings.vue'
import {
  fetchCampaigns,
  fetchMyCampaigns,
  fetchCampaignLeads,
  updateMyCampaignLeadStatus,
  assignLeadToAgent,
  runCampaignAICalls,
  editCampaign,
} from '@/api/campaignsApi'
import DashboardDigestSettings from '@/components/Shared/DashboardDigestSettings.vue'
import { triggerDashboardDigestNow } from '@/api/dashboardDigestApi'
import {
  createLead,
  generateLeadComments,
  getLeadHistory,
  getLeadCallHistory,
  getLeadBehaviorProfile,
} from '@/api/leadsApi'
import LeadFormDialog from '@/components/BusinessQueries/Dialogs/LeadFormDialog.vue'
import { initiateSoftphoneCall } from '@/api/softphoneApi'
import {
  callNumber,
  getSoftphoneStatus,
  hangupSoftphoneCall,
  isSoftphoneRegistered,
  prepareSoftphoneMedia,
  sendSoftphoneDtmf,
  setSoftphoneMuted,
  startSoftphoneSession,
} from '@/services/softphone'
import { fetchContexts } from '@/api/contextsApi'
import { fetchOrgUsers } from '@/api/usersApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { unwrapApiList } from '@/utils/graphql'
import {
  normalizeContextsList,
} from '@/utils/contexts'
import {
  STATUS_API_FIELD_KEYS,
  buildFieldDefsFromDefinitions,
  cleanLeadDisplay,
  humanDisplayLabel,
  resolveLeadFieldValue,
} from '@/utils/leadFields'
import {
  coerceFieldValueForApi,
  epochToDateInputValue,
  formatTypedFieldDisplay,
} from '@/utils/leadFieldTypes'
import { normalizeUserRecord } from '@/utils/session'
import { loadOrgDetailsIntoStore } from '@/utils/orgDetailsLoader'
import { isAiAgent, resolveListAgentType } from '@/utils/orgUsers'
import {
  normalizeCampaignsList,
  normalizeCampaignRecord,
  normalizeCampaignLeadsList,
  isCampaignActiveForLeadUpdates,
} from '@/utils/campaigns'
import {
  buildStatusOptions,
  businessQueryStatusLabel,
  businessQueryStatusTone,
  leadScoreTone,
} from '@/utils/businessQueries'
import {
  buildLeadActivityFeed,
  computeLeadHistoryMetrics,
  normalizeGenerateLeadComments,
  normalizeLeadCallHistoryList,
  normalizeLeadStatusHistoryList,
} from '@/utils/leadCallHistory'
import { formatLabel } from '@/utils/format'
import '@/styles/campaigns/campaign-workspace.css'
// Only for the .tk-actions-* action-menu pattern (trigger button + dropdown
// list), reused as-is from CampaignsPage.vue's row-actions menu instead of
// duplicating ~130 lines of CSS -- the topbar's "Actions" menu below is the
// same pattern, one level up (page actions instead of a table row's).
import '@/styles/campaigns/campaigns.css'

defineOptions({ name: 'CampaignDetailPage' })

const store = useStore()
const route = useRoute()
const router = useRouter()

const snackbar = ref({})
const campaign = ref(null)
const savingDigest = ref(false)
const digestSendNow = ref(false)
const digestSettings = ref({
  send_dashboard_digest_by_email: false,
  dashboard_digest_email_addresses: [],
  send_dashboard_digest_by_whatsapp: false,
  dashboard_digest_whatsapp_numbers: [],
  dashboard_digest_cc_agents: false,
  dashboard_digest_times: [],
  dashboard_digest_weekly_enabled: false,
  dashboard_digest_weekly_day: '',
  dashboard_digest_weekly_time: '',
  dashboard_digest_monthly_enabled: false,
  dashboard_digest_monthly_day: null,
  dashboard_digest_monthly_time: '',
})
const leads = ref([])
const loading = ref(false)
const agents = ref([])
const contexts = ref([])
const showEditDialog = ref(false)
const showBulkAssignDialog = ref(false)
const showBulkStatusDialog = ref(false)
const showCreateLeadDialog = ref(false)
// Landing straight on Settings right after creating a campaign (see
// CampaignsPage.vue's onCampaignCreated) so everything that used to live in
// the create dialog's "circus" is right where the admin expects it. Checked
// inline rather than via the isAdmin computed below (not yet declared here,
// and the Settings tab/pane are admin-only) -- same role check either way.
const canLandOnSettings = ['ADMIN', 'OWNER'].includes(
  String(store.state.userRole ?? '').trim().toUpperCase(),
)
const pageTab = ref(canLandOnSettings && route.query.tab === 'settings' ? 'settings' : 'leads')
const leadTab = ref('details')

const search = ref('')
const statusFilter = ref('')
const assigneeFilter = ref('')
/** Admin only: 'all' → getTaskLeads all_leads=true; 'mine' → own leads. */
const adminLeadScope = ref('all')
const selectedId = ref('')

const assigningId = ref('')
const runningAiCalls = ref(false)
const showRunAiCallsResult = ref(false)
const runAiCallsResult = ref(null)

const showCallDialog = ref(false)
const callDialogLead = ref(null)
const callDialogStatus = ref('')
const callStatusMenuOpen = ref(false)
const callDialogComments = ref('')
const callDialogFields = reactive({})
const savingCallStatus = ref(false)
const dialNumber = ref('')
const dialError = ref('')
const placingSipCall = ref(false)
const activeCallUuid = ref('')
const aiSuggestLoading = ref(false)
const aiSuggestAvailable = ref(false)
const aiSuggestMessage = ref('')
const aiSuggestions = ref(null)
const selectedStatusHistory = ref([])
const selectedCallHistory = ref([])
const selectedHistoryLoading = ref(false)
// Per-org, cross-campaign profile (communication style + relationship
// history) for this lead's own phone number -- same one the AI reads at
// call setup. Independent of this campaign's own history/overview above.
const selectedBehaviorProfile = ref('')
const selectedActivityFeed = computed(() =>
  buildLeadActivityFeed(selectedCallHistory.value, selectedStatusHistory.value),
)
let aiSuggestRequestToken = 0

const editingWorkspaceFields = ref(false)
const savingWorkspaceFields = ref(false)
const workspaceFields = reactive({})
const workspaceStatus = ref('')
const workspaceStatusComments = ref('')
const workspaceCallbackDate = ref('')
const workspaceCallbackNote = ref('')
const savingWorkspaceStatus = ref(false)
// Set when editing an AI-written ("SYSTEM"-labeled) activity entry -- ties
// the next saveWorkspaceStatus() submission back to that specific call
// instead of creating an unrelated new history entry.
const pendingEditCallUuid = ref('')
const tomorrowDateInputValue = computed(() => {
  const d = new Date(Date.now() + 24 * 60 * 60 * 1000)
  return d.toISOString().slice(0, 10)
})

const openSections = reactive({
  info: true,
  status: true,
  assign: false,
})

const callActive = ref(false)
const callPhase = ref('idle')
const callDurationSec = ref(0)
const callMuted = ref(false)
const showKeypad = ref(false)
const softphoneReady = ref(false)
/** True after Update status API succeeds in the open call dialog. */
const leadStatusUpdatedInDialog = ref(false)

const keypadDigits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#']

let softphonePoll = null
let processingPollTimer = null
const PROCESSING_POLL_MS = 4000

const campaignId = computed(() => String(route.params.campaignId ?? '').trim())
const isSessionReady = computed(() => store.getters.isSessionReady)
const currentUserId = computed(() => String(store.state.currentUserId ?? ''))

const isAdmin = computed(() => {
  const role = String(store.state.userRole ?? '').trim().toUpperCase()
  return role === 'ADMIN' || role === 'OWNER'
})

const isCampaignProcessing = computed(
  () => String(campaign.value?.status ?? '').toUpperCase() === 'PROCESSING',
)

const leadStatusGate = computed(() =>
  isCampaignActiveForLeadUpdates(campaign.value),
)

const canUpdateLeadStatuses = computed(() => leadStatusGate.value.ok)

// KAN-208: the backend's own ownership check on updateMyTaskLeadStatus
// (mirrored exactly here -- see that function's comment) throws "This lead
// is not assigned to you within this task" for a MANUAL/ROUND_ROBIN campaign
// unless the lead is assigned to you or you created the campaign -- but
// nothing on this page told an agent that BEFORE they opened "Edit fields"
// and filled in the form, so they'd hit a confusing error only on Save.
// Deliberately does NOT grant a general isAdmin bypass: the backend doesn't
// either, so an admin who isn't this campaign's creator gets rejected there
// too -- granting it here would just move the same confusing late failure
// to a different set of users instead of fixing it.
const canEditSelectedLead = computed(() => {
  const lead = selected.value
  if (!lead) return false
  const mode = String(campaign.value?.distributionMode ?? '').toUpperCase()
  if (mode === 'OPEN') return true
  const assignee = String(lead.assignedAgentId ?? '').trim()
  if (assignee && assignee === currentUserId.value) return true
  const creator = String(campaign.value?.createdBy ?? '').trim()
  return Boolean(creator && creator === currentUserId.value)
})

const editSelectedLeadBlockedReason =
  'This lead is assigned to another agent — you can\'t edit it.'

const campaignLifecycleBanner = computed(() => {
  const status = String(campaign.value?.status ?? '').trim().toUpperCase()
  if (status === 'COMPLETED') {
    return {
      tone: 'success',
      icon: 'mdi-check-decagram-outline',
      title: 'Campaign completed',
      // Individual lead edits and Run AI calls stay locked while COMPLETED,
      // but Bulk status is deliberately still available here -- it's the
      // recovery path: reset a lead back to Pending and the campaign
      // reopens to Active on its own within a few minutes, unlocking
      // everything else again.
      body: 'Every lead has been worked. Use Bulk status to reset a lead back to Pending if this campaign needs to reopen.',
    }
  }
  if (status === 'EXPIRED') {
    return {
      tone: 'danger',
      icon: 'mdi-calendar-remove-outline',
      title: 'Campaign expired',
      body: 'The end date passed with some leads still unworked. Lead status updates are closed.',
    }
  }
  return null
})

const isHumanCampaign = computed(
  () => String(campaign.value?.handledBy ?? '').toUpperCase() === 'HUMAN_AGENT',
)
// Gates the Leads-tab call-progress strip -- QUEUED/IN_PROGRESS/DONE is
// derived from dial_attempt_count/request_status, which only the AI dialer
// ever stamps (see getTaskLeads' callProgress comment). A HUMAN_AGENT
// campaign's leads never move dial_attempt_count off 0, so every lead would
// read "Queued" forever -- misleading, not just unused.
const isAiCampaignForProgress = computed(
  () => String(campaign.value?.handledBy ?? '').toUpperCase() === 'AI_AGENT',
)

// Restricted to schedule_type ONE_TIME specifically (a RECURRING_REMINDER
// Restricted to the Campaign Type toggle's own "One-time campaign" option
// (task_type STANDARD, a plain lead list with a start/end window, no
// due-date scheduling) -- NOT the Schedule sub-option "Once, at a time on
// the lead" under Recurring reminder (schedule_type ONE_TIME, the C1-C6
// due-date-per-lead mechanism). Explicit final call 2026-08-13, reversing
// an earlier decision that went the other way -- every RECURRING_REMINDER
// campaign (including schedule_type ONE_TIME) is hidden now, only STANDARD
// shows it.
// KAN-81: the org's one designated inbound campaign (org.default_incoming_task_id)
// only ever gets leads by someone calling in -- there's nothing to proactively
// dial, so a "Run AI calls" button here promised an action that never did
// anything meaningful. Same isDefaultInboundCampaign check already used to
// hide Call script on this same campaign (CampaignAdvancedSettings.vue) and
// to hide pause/delete on the list page (CampaignsPage.vue).
const isDefaultInboundCampaign = computed(() => {
  const defaultId = String(store.state.organization?.default_incoming_task_id ?? '').trim()
  return Boolean(campaignId.value) && campaignId.value === defaultId
})

const canRunAiCalls = computed(
  () =>
    isAdmin.value &&
    !isCampaignProcessing.value &&
    !isDefaultInboundCampaign.value &&
    String(campaign.value?.handledBy ?? '').toUpperCase() === 'AI_AGENT' &&
    String(campaign.value?.taskType ?? '').toUpperCase() === 'STANDARD',
)

const canEditCampaign = computed(() => {
  if (isAdmin.value) return true
  const creator = String(campaign.value?.createdBy ?? '').trim()
  return Boolean(creator && creator === currentUserId.value)
})

// KAN-16: reassigning a lead to a different agent is a HUMAN_AGENT concept --
// an AI campaign's "agent" is really a calling extension the dialer picks by
// its own throttle logic, not something an admin hand-assigns per lead. Also
// scoped to the "All campaigns" lead view (adminLeadScope 'all'), not "My
// campaigns" ('mine') -- an agent looking at their own queue shouldn't be
// hand off leads to other agents from inside their own view.
const canAssignLeads = computed(
  () =>
    canEditCampaign.value &&
    String(campaign.value?.distributionMode ?? '').toUpperCase() === 'MANUAL' &&
    String(campaign.value?.handledBy ?? '').toUpperCase() === 'HUMAN_AGENT' &&
    adminLeadScope.value === 'all',
)

const canBulkUpdateStatus = computed(() => canEditCampaign.value)

const assignableAgents = computed(() =>
  agents.value
    .filter((user) => String(user.role ?? '').trim().toUpperCase() === 'AGENT')
    .filter((user) =>
      String(campaign.value?.handledBy ?? '').toUpperCase() === 'AI_AGENT'
        ? isAiAgent(resolveListAgentType(user))
        : !isAiAgent(resolveListAgentType(user)),
    ),
)

const campaignContexts = computed(() => {
  const names = new Set(
    (Array.isArray(campaign.value?.contextNames)
      ? campaign.value.contextNames
      : []
    ).map((name) => String(name ?? '').trim().toLowerCase()).filter(Boolean),
  )
  if (!names.size) return contexts.value
  return contexts.value.filter((context) =>
    names.has(String(context.context_name ?? context.contextName ?? '').trim().toLowerCase()),
  )
})

// Exact (correctly-cased) context names for preselecting the create-lead
// dialog — only contexts whose product-level setting allows manual lead
// creation (allow_manual_leads; absent means enabled).
const campaignContextNames = computed(() =>
  campaignContexts.value
    .filter((context) => context.allow_manual_leads !== false)
    .map((context) => String(context.context_name ?? context.contextName ?? '').trim())
    .filter(Boolean),
)

// The button only appears when at least one of the campaign's products
// permits manual lead creation.
const canCreateLeadHere = computed(
  () => canEditCampaign.value && campaignContextNames.value.length > 0,
)

// The topbar consolidates every action (Edit/Bulk assign/Bulk status/
// Create lead/Run AI calls/Refresh) into one "Actions" menu instead of a
// row of separate buttons -- with more tabs added over time (Slot booking,
// Bookings), the old row of buttons squeezed the tab strip itself ("this
// tabs call is not looking good... so many buttons on top right" -- Ramu,
// 2026-09-04). Hides the trigger entirely when nothing inside it would
// actually apply (a viewer with no permitted actions on a tab where
// Refresh doesn't show either), rather than showing an empty menu.
const showTopbarRefresh = computed(
  () => pageTab.value !== 'dashboard' && pageTab.value !== 'callHistory' && pageTab.value !== 'dialAttempts',
)
const hasAnyTopbarAction = computed(
  () =>
    canEditCampaign.value ||
    canAssignLeads.value ||
    canBulkUpdateStatus.value ||
    canCreateLeadHere.value ||
    canRunAiCalls.value ||
    showTopbarRefresh.value,
)

// Creating a lead here writes it at the context level (normal createLead),
// tagged with this campaign's task_id so the backend attaches it here
// directly regardless of auto_adopt_matching_leads/filters (this IS its
// origin, not something adopted from elsewhere) -- every OTHER matching
// active campaign still only picks it up via the normal auto-adopt path.
async function saveCampaignLead({ form }) {
  const response = await createLead({ ...form, task_id: campaignId.value })
  if (!isApiSuccess(response)) {
    return { ok: false, error: getApiMessage(response, 'Failed to create lead') }
  }
  return {
    ok: true,
    message: getApiMessage(response, 'Lead created and added to campaign'),
    requestId: response?.data?.request_id || '',
  }
}

async function onCampaignLeadCreated(result) {
  // The dialog shows its own success snackbar -- reload so the new lead's
  // auto-adopted campaign membership (assignment, distribution mode, etc.)
  // is reflected, then surface it the way an agent actually wants to see a
  // freshly created lead: pinned to the top of the queue with its detail
  // view already open, not buried wherever the server happened to return it.
  void ensureOrgFieldDefinitions()
  void loadCampaign()
  await loadLeads()

  const requestId = String(result?.requestId || '').trim()
  if (!requestId) return
  const index = leads.value.findIndex((lead) => lead.requestId === requestId)
  if (index <= 0) {
    if (index === 0) selectLead(leads.value[0])
    return
  }
  const [newLead] = leads.value.splice(index, 1)
  leads.value.unshift(newLead)
  selectLead(newLead)
}

const metricsConvertedStatus = computed(() => {
  for (const context of campaignContexts.value) {
    const statuses = Array.isArray(context?.lead_statuses)
      ? context.lead_statuses
      : Array.isArray(context?.leadStatuses)
        ? context.leadStatuses
        : []
    const match = statuses.find((status) => {
      const value = String(
        typeof status === 'string' ? status : status?.value ?? status?.status ?? '',
      )
        .trim()
        .toUpperCase()
      return value === 'CONVERTED' || value.includes('CONVERT')
    })
    if (match) {
      return String(
        typeof match === 'string' ? match : match?.value ?? match?.status ?? 'CONVERTED',
      )
        .trim()
        .toUpperCase()
    }
  }
  return 'CONVERTED'
})

// Queued / In progress / Done tallies for the Leads-tab summary strip --
// counted client-side off `leads`, the same rows already fetched for the
// list below (no separate call, mirrors how CampaignDialAttemptsPanel tallies
// connectedCount/notAnsweredCount off its own already-fetched attempts).
// Deliberately reads `leads` (whatever scope was fetched -- all vs "my
// leads", see shouldFetchAllLeads), not `filteredLeads` -- search/status
// filters shouldn't make the campaign-level progress picture jump around.
const queuedLeadsCount = computed(
  () => leads.value.filter((lead) => lead.callProgress === 'QUEUED').length,
)
const inProgressLeadsCount = computed(
  () => leads.value.filter((lead) => lead.callProgress === 'IN_PROGRESS').length,
)
const doneLeadsCount = computed(
  () => leads.value.filter((lead) => lead.callProgress === 'DONE').length,
)
// Calm/positive state -- nothing queued and nothing ringing means the
// campaign has been fully worked, not that the data is missing or broken.
// Guarded on leads.value.length so an empty/not-yet-loaded list (which would
// also read as 0/0) never gets mistaken for "fully worked".
const campaignFullyWorked = computed(
  () =>
    leads.value.length > 0 &&
    queuedLeadsCount.value === 0 &&
    inProgressLeadsCount.value === 0,
)

const statusFilterOptions = computed(() => {
  const seen = new Set(leads.value.map((lead) => lead.status).filter(Boolean))
  return [
    { value: '', title: 'All statuses' },
    ...[...seen].sort().map((status) => ({
      value: status,
      title: businessQueryStatusLabel(status),
    })),
  ]
})

const assigneeFilterOptions = computed(() => {
  const options = [{ value: '', title: 'Everyone' }]

  // Prefer agents on this task; fall back to all matching agents if unset.
  const campaignAgentIds = (Array.isArray(campaign.value?.assignedAgentIds)
    ? campaign.value.assignedAgentIds
    : []
  )
    .map((id) => String(id ?? '').trim())
    .filter(Boolean)

  const agentRows = campaignAgentIds.length
    ? campaignAgentIds.map((id) => {
        const agent = agents.value.find((user) => String(user.id) === id)
        return {
          value: id,
          title: humanDisplayLabel(agent?.name, agent?.email) || 'Agent',
        }
      })
    : assignableAgents.value.map((agent) => ({
        value: String(agent.id),
        title: humanDisplayLabel(agent.name, agent.email) || 'Agent',
      }))

  agentRows.sort((a, b) =>
    String(a.title).localeCompare(String(b.title), undefined, {
      sensitivity: 'base',
    }),
  )
  options.push(...agentRows)
  return options
})

/** OPEN = any agent on the task can work any lead; MANUAL/RR = only assigned. */
const agentSeesAssignedOnly = computed(() => {
  if (isAdmin.value) return false
  const mode = String(campaign.value?.distributionMode ?? '').toUpperCase()
  return mode === 'MANUAL' || mode === 'ROUND_ROBIN' || mode === 'EXCEL' || mode === 'FIELD_BASED'
})

const filteredLeads = computed(() => {
  const q = String(search.value || '').trim().toLowerCase()
  const assigneeScope = isAdmin.value
    ? assigneeFilter.value
    : agentSeesAssignedOnly.value
      ? currentUserId.value || '__none__'
      : ''

  return leads.value.filter((lead) => {
    if (statusFilter.value && lead.status !== statusFilter.value) return false
    const leadAssignee = String(lead.assignedAgentId ?? '').trim()
    if (assigneeScope === '__none__') {
      return false
    }
    if (assigneeScope && leadAssignee !== String(assigneeScope).trim()) {
      return false
    }
    if (!q) return true
    return [
      lead.name,
      lead.phone,
      lead.phoneRaw,
      lead.email,
      lead.contextName,
      lead.statusLabel,
      assignedAgentLabel(lead),
    ].some((value) => String(value ?? '').toLowerCase().includes(q))
  })
})

const selected = computed(
  () => leads.value.find((lead) => lead.id === selectedId.value) || null,
)

const selectedAiMetrics = computed(() => {
  const lead = selected.value
  const history = Array.isArray(selectedStatusHistory.value)
    ? selectedStatusHistory.value
    : []
  const metrics = computeLeadHistoryMetrics(history, lead)
  const score = metrics.score
  const scoreTone = leadScoreTone(score) || 'neutral'
  const clamped =
    score == null ? 0 : Math.max(0, Math.min(100, Number(score) || 0))
  const ringColor =
    scoreTone === 'success'
      ? '#16a34a'
      : scoreTone === 'warning'
        ? '#d97706'
        : scoreTone === 'error'
          ? '#dc2626'
          : 'rgba(107, 114, 128, 0.35)'

  return {
    score,
    scoreLabel: score != null ? String(score) : '—',
    scoreTone,
    scoreRingStyle: {
      background: `conic-gradient(${ringColor} ${clamped * 3.6}deg, rgba(17, 24, 39, 0.08) 0deg)`,
    },
    overview: String(lead?.leadOverview ?? '').trim(),
    bestTimeLabel: String(lead?.bestTimeToCallLabel ?? '').trim(),
    totalCalls: metrics.totalCalls,
    talkTimeLabel: metrics.talkTimeLabel,
    lastCallStatusLabel: formatCallStatusLabel(metrics.lastCallStatusLabel),
  }
})

/**
 * Read-only view of this lead's internal fields (e.g. a reminder anchor
 * date like follow_up_call) -- hidden from Add lead/Edit lead/tables
 * everywhere else, but still worth surfacing here so an agent can see
 * what's actually set without being able to hand-edit it.
 */
const selectedInternalFields = computed(() => {
  const lead = selected.value
  if (!lead) return []
  const contextDefs = resolveLeadContext(lead)?.custom_field_definitions
  if (!contextDefs || typeof contextDefs !== 'object') return []

  return buildFieldDefsFromDefinitions(contextDefs, { includeInternal: true })
    .filter((field) => field.isInternal)
    .map((field) => {
      const raw = resolveLeadFieldValue(lead, field.key)
      const display =
        raw === '' || raw == null
          ? ''
          : formatTypedFieldDisplay(raw, field.type) || String(raw)
      return { key: field.key, label: field.label, display }
    })
})

function formatCallStatusLabel(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return '—'
  return formatLabel(raw)
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
}

const callDialogStatusOptions = computed(() => {
  const options = buildStatusOptions(
    resolveLeadContext(callDialogLead.value)?.lead_statuses,
  )
  const current = String(callDialogStatus.value || '').trim().toUpperCase()
  if (
    current &&
    !options.some((opt) => String(opt.value).toUpperCase() === current)
  ) {
    return [
      { value: current, title: businessQueryStatusLabel(current) },
      ...options,
    ]
  }
  return options
})

const callDialogStatusLabel = computed(() => {
  const current = String(callDialogStatus.value || '').trim().toUpperCase()
  if (!current) return ''
  const match = callDialogStatusOptions.value.find(
    (opt) => String(opt.value).toUpperCase() === current,
  )
  return match?.title || businessQueryStatusLabel(current)
})

function selectCallDialogStatus(value) {
  callDialogStatus.value = String(value || '').trim().toUpperCase()
  callStatusMenuOpen.value = false
}

const workspaceStatusOptions = computed(() =>
  buildStatusOptions(resolveLeadContext(selected.value)?.lead_statuses),
)

const canPlaceSipCall = computed(() =>
  Boolean(String(dialNumber.value || '').trim()) &&
  !callActive.value &&
  !placingSipCall.value,
)

/** Context field defs that already have a value on this lead (hide empty). */
const callDialogFieldDefs = computed(() =>
  callDialogEditableFieldDefs(callDialogLead.value),
)

const workspaceFieldDefs = computed(() =>
  fieldDefsPresentOnLead(selected.value),
)

const infoFields = computed(() => {
  const lead = selected.value
  if (!lead) return []
  return workspaceFieldDefs.value.map((field) => {
    const raw = resolveLeadFieldValue(lead, field.key)
    const type = String(field.type ?? '').toUpperCase()
    const callable = type === 'PHONE_NUMBER' || field.key === 'contact_number'
    const dialValue = callable
      ? String(raw ?? '')
          .replace(/[^\d+]/g, '')
          .trim()
      : ''
    return {
      key: field.key,
      label: field.label,
      long: Boolean(field.long),
      type,
      callable,
      dialValue,
      value:
        formatTypedFieldDisplay(raw, type) ||
        String(raw ?? ''),
    }
  })
})

function initials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (!parts.length) return '?'
  return ((parts[0][0] || '') + (parts[1]?.[0] || '')).toUpperCase()
}

function priorityClass(lead) {
  const raw = String(lead?.customFields?.priority || '').toLowerCase()
  if (raw.includes('high') || (lead?.leadScore != null && lead.leadScore >= 70)) {
    return 'tw-priority--high'
  }
  if (raw.includes('low') || (lead?.leadScore != null && lead.leadScore < 40)) {
    return 'tw-priority--low'
  }
  if (raw || lead?.leadScore != null) return 'tw-priority--medium'
  return ''
}

function formatDuration(totalSec) {
  const sec = Math.max(0, Number(totalSec) || 0)
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function assignedAgentLabel(lead) {
  const fromStored = humanDisplayLabel(lead?.assignedAgentName)
  if (fromStored) return fromStored
  const id = String(lead?.assignedAgentId ?? '').trim()
  if (!id) return ''
  const agent = agents.value.find((user) => String(user.id) === id)
  return humanDisplayLabel(agent?.name, agent?.email)
}

/** Meta under lead cards — never show "Unassigned". */
function leadMetaLine(lead) {
  const parts = []
  const assignee = assignedAgentLabel(lead)
  if (assignee) parts.push(assignee)
  if (lead?.createdOnLabel && lead.createdOnLabel !== '—') parts.push(lead.createdOnLabel)
  return parts.join(' · ')
}

function isDropdownField(field) {
  const type = String(field?.type ?? '').trim().toUpperCase()
  return type === 'DROPDOWN' || type === 'SELECT' || type === 'DROP_DOWN'
}

function isValidDropdownSelection(field, value) {
  if (!isDropdownField(field)) return true
  const selected = String(value ?? '').trim()
  if (!selected) return false
  const options = Array.isArray(field.options) ? field.options : []
  if (!options.length) return true
  return options.includes(selected)
}

function sanitizeFormFieldValue(raw, field) {
  if (field.type === 'DATE') return epochToDateInputValue(raw) || ''
  const value = raw ?? ''
  if (!isDropdownField(field)) return value
  const selected = String(value).trim()
  if (!selected) return ''
  return isValidDropdownSelection(field, selected) ? selected : ''
}

/** Call dialog: editable product fields — dropdowns always shown so agents can set missing values. */
function callDialogEditableFieldDefs(lead) {
  if (!lead) return []
  return buildLeadFieldDefsForLead(lead).filter((field) => {
    if (field.locked) return false
    if (STATUS_API_FIELD_KEYS.has(field.key)) return false
    if (isDropdownField(field)) return true
    return Boolean(resolveLeadFieldValue(lead, field.key))
  })
}

function resolveLeadContext(lead) {
  if (!lead) return null
  if (lead.contextId) {
    const byId = contexts.value.find(
      (item) => String(item.context_id ?? '') === String(lead.contextId),
    )
    if (byId) return byId
  }
  const name = String(lead.contextName ?? '').trim().toLowerCase()
  if (name) {
    const byName = contexts.value.find(
      (item) => String(item.context_name ?? '').trim().toLowerCase() === name,
    )
    if (byName) return byName
  }
  const contextNames = Array.isArray(campaign.value?.contextNames) ? campaign.value.contextNames : []
  if (contextNames.length === 1) {
    const only = String(contextNames[0] ?? '').trim().toLowerCase()
    const byContextName = contexts.value.find(
      (item) => String(item.context_name ?? '').trim().toLowerCase() === only,
    )
    if (byContextName) return byContextName
  }
  if (contexts.value.length === 1) return contexts.value[0]
  return null
}

/**
 * Dynamically bind every enabled field from listContexts →
 * custom_field_definitions (system + custom). Fall back to org defaults.
 */
function buildLeadFieldDefsForLead(lead) {
  const context = resolveLeadContext(lead)
  const contextDefs = context?.custom_field_definitions
  if (contextDefs && typeof contextDefs === 'object' && Object.keys(contextDefs).length) {
    return buildFieldDefsFromDefinitions(contextDefs)
  }
  return buildFieldDefsFromDefinitions(
    store.state.organization?.default_field_definitions,
  )
}

/** Keep only product fields that have a non-empty value on this lead. */
function fieldDefsPresentOnLead(lead) {
  if (!lead) return []
  return buildLeadFieldDefsForLead(lead).filter((field) =>
    Boolean(resolveLeadFieldValue(lead, field.key)),
  )
}

function canCallLead(lead) {
  if (!isHumanCampaign.value) return false
  if (lead.callableAgain === false) return false
  if (!String(lead.requestId ?? '').trim()) return false
  if (isAdmin.value) return true
  const mode = String(campaign.value?.distributionMode ?? '').toUpperCase()
  if (mode === 'OPEN') return true
  return String(lead.assignedAgentId ?? '') === currentUserId.value
}

function showSnack(type, message) {
  snackbar.value = {
    SnackbarVmodel: true,
    SnackbarColor: type === 'error' ? 'error' : 'success',
    SnackbarText: message,
  }
}

function goBack() {
  router.push({ name: 'Campaigns' })
}

// Admins land on the Settings tab, which now carries every field this old
// popup dialog used to own (name, product, dates/schedule, handled by,
// agents) plus everything that already lived there -- one place instead of
// two. The Settings tab itself is admin-only, though (dashboard digest
// routing, daily dial cap overrides, etc.), so a non-admin campaign creator
// -- who can still edit their own campaign per canEditCampaign, just not
// see Settings -- keeps the original dialog instead of landing on a tab
// that would render as blank for them.
function onEditClick() {
  if (isAdmin.value) {
    pageTab.value = 'settings'
  } else {
    showEditDialog.value = true
  }
}

function toggleSection(key) {
  openSections[key] = !openSections[key]
}

function selectLead(lead) {
  selectedId.value = lead.id
  editingWorkspaceFields.value = false
  savingWorkspaceFields.value = false
  workspaceStatus.value = String(lead?.status || '').trim().toUpperCase()
  workspaceStatusComments.value = ''
}

function leadDialNumber(lead) {
  // .phone first, .phoneRaw as fallback -- matches every OTHER display of a
  // lead's number on this page (name badge, lead list row, detail panel).
  // This used to read phoneRaw first; when the two fields disagree for a
  // lead, that meant the dial pad silently pre-filled a different number
  // than the one the agent had just visually confirmed before calling.
  return String(lead?.phone || lead?.phoneRaw || '')
    .replace(/[^\d+]/g, '')
    .trim()
}

function onLeadListCall(lead) {
  if (!canCallLead(lead)) return
  const number = leadDialNumber(lead)
  if (!number) return
  void openCallDialog(lead, number)
}

function displayLeadName(lead) {
  return cleanLeadDisplay(lead?.name) || 'Lead'
}

function hydrateFieldsInto(target, lead, fieldDefs) {
  const next = {}
  for (const field of fieldDefs) {
    const raw = resolveLeadFieldValue(lead, field.key)
    next[field.key] = sanitizeFormFieldValue(raw, field)
  }

  Object.keys(target).forEach((key) => {
    delete target[key]
  })
  Object.assign(target, next)
}

function buildFieldsPayload(source, fieldDefs) {
  const fields = {}
  for (const field of fieldDefs) {
    if (field.locked) continue
    if (STATUS_API_FIELD_KEYS.has(field.key)) continue
    const raw = source[field.key]
    if (raw == null || String(raw).trim() === '') continue
    if (!isValidDropdownSelection(field, raw)) continue
    const value = coerceFieldValueForApi(raw, field.type)
    if (value == null || value === '') continue
    fields[field.key] = value
  }
  return fields
}

function hydrateCallDialogFields(lead) {
  hydrateFieldsInto(callDialogFields, lead, callDialogFieldDefs.value)
}

function buildCallDialogFieldsPayload() {
  return buildFieldsPayload(callDialogFields, callDialogFieldDefs.value)
}

function startWorkspaceFieldEdit() {
  if (!selected.value) return
  if (!canEditSelectedLead.value) {
    showSnack('error', editSelectedLeadBlockedReason)
    return
  }
  openSections.info = true
  hydrateFieldsInto(workspaceFields, selected.value, workspaceFieldDefs.value)
  editingWorkspaceFields.value = true
}

function cancelWorkspaceFieldEdit() {
  editingWorkspaceFields.value = false
  Object.keys(workspaceFields).forEach((key) => {
    delete workspaceFields[key]
  })
}

async function saveWorkspaceFields() {
  const lead = selected.value
  const requestId = String(lead?.requestId ?? '').trim()
  if (!campaignId.value || !requestId) return

  const fields = buildFieldsPayload(workspaceFields, workspaceFieldDefs.value)
  if (!Object.keys(fields).length) {
    showSnack('error', 'Enter at least one field to update.')
    return
  }
  if (!canUpdateLeadStatuses.value) {
    showSnack('error', leadStatusGate.value.reason || 'Lead updates are closed.')
    return
  }
  if (!canEditSelectedLead.value) {
    showSnack('error', editSelectedLeadBlockedReason)
    return
  }

  const status = String(lead.status || 'PENDING').trim().toUpperCase() || 'PENDING'
  savingWorkspaceFields.value = true
  try {
    const response = await updateMyCampaignLeadStatus({
      task_id: campaignId.value,
      request_id: requestId,
      request_status: status,
      comments: 'Updated lead fields from workspace.',
      fields,
    })
    if (!isApiSuccess(response)) {
      showSnack('error', getApiMessage(response, 'Failed to update lead fields'))
      return
    }

    const local = leads.value.find((item) => item.requestId === requestId)
    if (local) Object.assign(local, applyFieldsToLocalLead(local, fields))
    editingWorkspaceFields.value = false
    showSnack('success', getApiMessage(response, 'Lead fields updated.'))
  } finally {
    savingWorkspaceFields.value = false
  }
}

async function saveWorkspaceStatus() {
  const lead = selected.value
  const requestId = String(lead?.requestId ?? '').trim()
  const status = String(workspaceStatus.value || '').trim().toUpperCase()
  const comments = String(workspaceStatusComments.value || '').trim()

  if (!campaignId.value || !requestId) return
  if (!status) {
    showSnack('error', 'Select a status to update.')
    return
  }
  if (!comments) {
    showSnack('error', 'Comments are required for a status change.')
    return
  }
  if (!canUpdateLeadStatuses.value) {
    showSnack('error', leadStatusGate.value.reason || 'Status updates are closed.')
    return
  }

  const callbackDateStr = String(workspaceCallbackDate.value || '').trim()
  const callbackRequestedOn = callbackDateStr
    ? new Date(`${callbackDateStr}T09:00:00`).getTime()
    : null

  savingWorkspaceStatus.value = true
  try {
    const response = await updateMyCampaignLeadStatus({
      task_id: campaignId.value,
      request_id: requestId,
      request_status: status,
      comments,
      ...(pendingEditCallUuid.value ? { call_uuid: pendingEditCallUuid.value } : {}),
      ...(callbackRequestedOn ? {
        callback_requested_on: callbackRequestedOn,
        callback_note: workspaceCallbackNote.value,
      } : {}),
    })
    if (!isApiSuccess(response)) {
      showSnack('error', getApiMessage(response, 'Failed to update status'))
      return
    }

    const local = leads.value.find((item) => item.requestId === requestId)
    if (local) {
      local.status = status
      local.statusLabel = businessQueryStatusLabel(status)
      local.statusTone = businessQueryStatusTone(status)
      if (callbackRequestedOn) {
        local.hasCallbackRequested = true
        local.callbackRequestedOn = callbackRequestedOn
        local.callbackNote = workspaceCallbackNote.value
      }
    }
    workspaceCallbackDate.value = ''
    workspaceCallbackNote.value = ''
    workspaceStatusComments.value = ''
    pendingEditCallUuid.value = ''
    showSnack('success', getApiMessage(response, 'Status updated successfully.'))
    void loadSelectedLeadHistory()
  } finally {
    savingWorkspaceStatus.value = false
  }
}

// Pre-fills the Update-status form with an AI-written entry's own
// status/comments and ties the next save back to that exact call --
// otherwise editing would just log an unrelated new history entry instead
// of correcting the one the agent actually clicked "Edit" on.
function editAiHistoryEntry(entry) {
  workspaceStatus.value = String(entry.requestStatus || '').toUpperCase()
  workspaceStatusComments.value = entry.comments || ''
  pendingEditCallUuid.value = entry.callUuid || ''
  openSections.status = true
  showSnack('info', 'Editing the AI-written disposition -- update the comments/status below and save.')
}

function applyFieldsToLocalLead(lead, fields) {
  if (!lead || !fields || typeof fields !== 'object') return lead
  const next = { ...lead }
  if (fields.contact_name != null) next.name = String(fields.contact_name)
  if (fields.contact_email != null) next.email = String(fields.contact_email)
  if (fields.contact_number != null) {
    next.phoneRaw = String(fields.contact_number)
    next.phone = String(fields.contact_number)
  }
  if (fields.type_of_business != null) {
    next.typeOfBusiness = String(fields.type_of_business)
    next.company = String(fields.type_of_business)
  }
  if (fields.industry != null) next.industry = String(fields.industry)
  if (fields.message != null) next.message = String(fields.message)
  if (fields.comments != null) next.comments = String(fields.comments)

  const known = new Set([
    'contact_name',
    'contact_email',
    'contact_number',
    'type_of_business',
    'industry',
    'message',
    'comments',
  ])
  const custom = { ...(next.customFields || {}) }
  for (const [key, value] of Object.entries(fields)) {
    if (known.has(key)) continue
    custom[key] = value
  }
  next.customFields = custom
  return next
}

async function openCallDialog(lead, phoneOverride = '') {
  if (!lead) return
  callDialogLead.value = lead
  callDialogStatus.value = String(lead.status || '').trim().toUpperCase()
  callDialogComments.value = ''
  callStatusMenuOpen.value = false
  activeCallUuid.value = ''
  placingSipCall.value = false
  resetAiSuggestions()
  hydrateCallDialogFields(lead)
  const override = String(phoneOverride ?? '')
    .replace(/[^\d+]/g, '')
    .trim()
  dialNumber.value =
    override ||
    String(lead.phone || lead.phoneRaw || '')
      .replace(/[^\d+]/g, '')
  dialError.value = ''
  leadStatusUpdatedInDialog.value = false
  showCallDialog.value = true
  refreshSoftphoneUi()

  if (!isSoftphoneRegistered()) {
    dialError.value = 'Connecting softphone…'
    try {
      await startSoftphoneSession(currentUserId.value)
    } catch (_) {
      /* surfaced below */
    }
    refreshSoftphoneUi()
    if (!isSoftphoneRegistered()) {
      dialError.value =
        'Softphone is not ready yet — wait until status shows Ready, then press Call.'
    } else {
      dialError.value = ''
    }
  }
}

async function placeSipCall() {
  dialError.value = ''
  if (placingSipCall.value || callActive.value) return

  if (!String(dialNumber.value || '').trim()) {
    dialError.value = 'Enter a phone number to call.'
    return
  }

  const requestId = String(callDialogLead.value?.requestId ?? '').trim()
  if (!campaignId.value || !requestId) {
    dialError.value = 'Lead is missing an id — cannot start the call.'
    showSnack('error', dialError.value)
    return
  }

  placingSipCall.value = true
  try {
    if (!isSoftphoneRegistered()) {
      await startSoftphoneSession(currentUserId.value)
      refreshSoftphoneUi()
    }
    if (!isSoftphoneRegistered()) {
      dialError.value =
        'Softphone is not ready yet — wait until status shows Ready.'
      showSnack('error', dialError.value)
      return
    }

    // Same click-gesture mic unlock the FAB dialer relies on (best-effort).
    const mediaReady = prepareSoftphoneMedia({ timeoutMs: 3000 }).catch(
      (mediaError) => {
        console.warn(
          'Softphone mic prepare skipped',
          mediaError?.message || mediaError,
        )
        return null
      },
    )

    resetAiSuggestions()

    // Register with backend for call_uuid / recording link on status save.
    const response = await initiateSoftphoneCall({
      task_id: campaignId.value,
      request_id: requestId,
    })
    await Promise.race([mediaReady, new Promise((r) => setTimeout(r, 500))])

    if (!isApiSuccess(response)) {
      dialError.value = getApiMessage(response, 'Could not start the call.')
      showSnack('error', dialError.value)
      return
    }

    activeCallUuid.value = pickCallUuid(response)
    if (!activeCallUuid.value) {
      dialError.value =
        'Call started, but no call_uuid was returned — status can still be saved, recording will not link.'
    } else {
      dialError.value = ''
    }

    // Place the live SIP call the same way as the FAB dialer (browser dial-out).
    // Waiting for AMI to INVITE this UA fails even when SIP is registered.
    try {
      callNumber(dialNumber.value)
    } catch (sipError) {
      dialError.value = sipError?.message || 'Softphone dial failed.'
      showSnack('error', dialError.value)
      return
    }

    showSnack(
      'success',
      getApiMessage(response, 'Calling — softphone dialing the number.'),
    )
    refreshSoftphoneUi()
  } catch (error) {
    dialError.value = error?.message || 'Could not start the call.'
    showSnack('error', dialError.value)
  } finally {
    placingSipCall.value = false
  }
}

/**
 * After status is updated and the dialog closes: move that lead to the
 * end of the queue and select the next visible lead.
 */
function advanceQueueAfterCall(leadId) {
  const id = String(leadId || '').trim()
  if (!id) return

  const queue = filteredLeads.value
  const queueIdx = queue.findIndex((lead) => lead.id === id)
  const nextLead = queueIdx >= 0 ? queue[queueIdx + 1] || null : null

  const sourceIdx = leads.value.findIndex((lead) => lead.id === id)
  if (sourceIdx >= 0) {
    const [moved] = leads.value.splice(sourceIdx, 1)
    leads.value.push(moved)
  }

  if (nextLead) {
    selectLead(nextLead)
    return
  }

  // Was last in the visible queue — jump to the new first lead (if any).
  const first = filteredLeads.value.find((lead) => lead.id !== id) || filteredLeads.value[0]
  if (first) selectLead(first)
  else selectedId.value = ''
}

function closeCallDialog() {
  callStatusMenuOpen.value = false
  const leadId = callDialogLead.value?.id || selectedId.value
  const shouldAdvance = leadStatusUpdatedInDialog.value

  if (callActive.value) {
    hangupSoftphoneCall()
  }
  showCallDialog.value = false
  callDialogLead.value = null
  dialError.value = ''
  placingSipCall.value = false
  activeCallUuid.value = ''
  resetAiSuggestions()
  leadStatusUpdatedInDialog.value = false

  if (shouldAdvance) {
    advanceQueueAfterCall(leadId)
  }
}

function pressDialKey(digit) {
  dialError.value = ''
  if (callActive.value) {
    sendSoftphoneDtmf(digit)
    return
  }
  if (digit === '0' && !dialNumber.value) {
    dialNumber.value = '+'
    return
  }
  dialNumber.value += digit
}

/** Read call_uuid from initiateSoftphoneCall response (AWS JSON envelope). */
function pickCallUuid(response) {
  const root = response && typeof response === 'object' ? response : {}
  const data =
    root.data && typeof root.data === 'object' && !Array.isArray(root.data)
      ? root.data
      : root
  const nested =
    data.data && typeof data.data === 'object' && !Array.isArray(data.data)
      ? data.data
      : null
  return String(
    data.call_uuid ??
      data.callUuid ??
      nested?.call_uuid ??
      nested?.callUuid ??
      root.call_uuid ??
      root.callUuid ??
      '',
  ).trim()
}

function resetAiSuggestions() {
  aiSuggestRequestToken += 1
  aiSuggestLoading.value = false
  aiSuggestAvailable.value = false
  aiSuggestMessage.value = ''
  aiSuggestions.value = null
}

function applyAiSuggestions() {
  const suggestion = aiSuggestions.value
  if (!suggestion) return
  if (suggestion.comments) {
    callDialogComments.value = suggestion.comments
  }
  if (suggestion.requestStatus) {
    const allowed = callDialogStatusOptions.value.some(
      (opt) => opt.value === suggestion.requestStatus,
    )
    if (allowed) callDialogStatus.value = suggestion.requestStatus
  }
}

async function requestAiCommentSuggestions() {
  const requestId = String(callDialogLead.value?.requestId ?? '').trim()
  const callUuid = String(activeCallUuid.value ?? '').trim()
  if (!requestId || !callUuid) return

  const token = ++aiSuggestRequestToken
  aiSuggestLoading.value = true
  aiSuggestAvailable.value = false
  aiSuggestMessage.value = ''
  aiSuggestions.value = null

  try {
    const response = await generateLeadComments({
      request_id: requestId,
      call_uuid: callUuid,
    })
    if (token !== aiSuggestRequestToken) return

    if (!isApiSuccess(response)) {
      aiSuggestMessage.value = getApiMessage(
        response,
        'Could not generate AI suggestions.',
      )
      return
    }

    const suggestion = normalizeGenerateLeadComments(response)
    aiSuggestions.value = suggestion
    aiSuggestAvailable.value = suggestion.available
    aiSuggestMessage.value = suggestion.available
      ? ''
      : suggestion.message || 'Transcript not ready yet — try again shortly.'

    if (suggestion.available) {
      if (!String(callDialogComments.value || '').trim() && suggestion.comments) {
        callDialogComments.value = suggestion.comments
      }
      if (!String(callDialogStatus.value || '').trim() && suggestion.requestStatus) {
        const allowed = callDialogStatusOptions.value.some(
          (opt) => opt.value === suggestion.requestStatus,
        )
        if (allowed) callDialogStatus.value = suggestion.requestStatus
      }
    }
  } finally {
    if (token === aiSuggestRequestToken) {
      aiSuggestLoading.value = false
    }
  }
}

async function saveCallDialogStatus() {
  const lead = callDialogLead.value
  const requestId = String(lead?.requestId ?? '').trim()
  const status = String(callDialogStatus.value || '').trim().toUpperCase()
  const comments = String(callDialogComments.value || '').trim()
  const fields = buildCallDialogFieldsPayload()

  if (!campaignId.value || !requestId) return
  if (!status) {
    showSnack('error', 'Select a status to update.')
    return
  }
  if (!comments) {
    showSnack('error', 'Comments are required.')
    return
  }
  if (!canUpdateLeadStatuses.value) {
    showSnack('error', leadStatusGate.value.reason || 'Status updates are closed.')
    return
  }

  savingCallStatus.value = true
  try {
    // Pass the softphone call_uuid from initiateSoftphoneCall so this
    // status/remarks entry links to the recording in lead history.
    const response = await updateMyCampaignLeadStatus({
      task_id: campaignId.value,
      request_id: requestId,
      request_status: status,
      comments,
      ...(activeCallUuid.value ? { call_uuid: activeCallUuid.value } : {}),
      ...(Object.keys(fields).length ? { fields } : {}),
    })
    if (!isApiSuccess(response)) {
      showSnack('error', getApiMessage(response, 'Failed to update status'))
      return
    }

    const local = leads.value.find((item) => item.requestId === requestId)
    if (local) {
      local.status = status
      local.statusLabel = businessQueryStatusLabel(status)
      local.statusTone = businessQueryStatusTone(status)
      local.comments = comments
      Object.assign(local, applyFieldsToLocalLead(local, fields))
    }
    if (callDialogLead.value?.requestId === requestId) {
      callDialogLead.value = applyFieldsToLocalLead(
        {
          ...callDialogLead.value,
          status,
          statusLabel: businessQueryStatusLabel(status),
          statusTone: businessQueryStatusTone(status),
          comments,
        },
        fields,
      )
      if (fields.contact_number) {
        dialNumber.value = String(fields.contact_number).replace(/[^\d+]/g, '')
      }
    }

    leadStatusUpdatedInDialog.value = true
    showSnack('success', getApiMessage(response, 'Status updated successfully.'))
    void loadSelectedLeadHistory()
    closeCallDialog()
  } finally {
    savingCallStatus.value = false
  }
}

async function onAssignSelect(event) {
  const agentId = String(event?.target?.value || '').trim()
  const agent = assignableAgents.value.find((item) => String(item.id) === agentId)
  if (!agent || !selected.value) return
  await assignLead(selected.value, agent)
}

async function assignLead(lead, agent) {
  const requestId = String(lead?.requestId ?? '').trim()
  const agentId = String(agent?.id ?? '').trim()
  if (!campaignId.value || !requestId || !agentId) return

  assigningId.value = requestId
  try {
    const response = await assignLeadToAgent({
      task_id: campaignId.value,
      request_id: requestId,
      agent_id: agentId,
    })
    if (!isApiSuccess(response)) {
      showSnack('error', getApiMessage(response, 'Failed to assign lead'))
      return
    }
    const local = leads.value.find((item) => item.requestId === requestId)
    if (local) {
      local.assignedAgentId = agentId
      local.assignedAgentName = humanDisplayLabel(agent?.name, agent?.email)
    }
    showSnack('success', getApiMessage(response, 'Lead assigned successfully.'))
  } finally {
    assigningId.value = ''
  }
}

function onCampaignEdited(message) {
  showSnack('success', typeof message === 'string' ? message : 'Campaign updated.')
  showEditDialog.value = false
  void refreshAll()
}

// Settings tab (admin-only, see isAdmin gate on the tab button itself) --
// Dashboard Digest lives here rather than inside the edit dialog, with its
// own load/save so it doesn't need the rest of the campaign form open.
function syncDigestSettingsFromCampaign() {
  const c = campaign.value
  digestSettings.value = {
    send_dashboard_digest_by_email: !!c?.sendDashboardDigestByEmail,
    dashboard_digest_email_addresses: Array.isArray(c?.dashboardDigestEmailAddresses)
      ? [...c.dashboardDigestEmailAddresses]
      : [],
    send_dashboard_digest_by_whatsapp: !!c?.sendDashboardDigestByWhatsapp,
    dashboard_digest_whatsapp_numbers: Array.isArray(c?.dashboardDigestWhatsappNumbers)
      ? [...c.dashboardDigestWhatsappNumbers]
      : [],
    dashboard_digest_cc_agents: !!c?.dashboardDigestCcAgents,
    dashboard_digest_times: Array.isArray(c?.dashboardDigestTimes)
      ? [...c.dashboardDigestTimes]
      : [],
    dashboard_digest_weekly_enabled: !!c?.dashboardDigestWeeklyEnabled,
    dashboard_digest_weekly_day: c?.dashboardDigestWeeklyDay || '',
    dashboard_digest_weekly_time: c?.dashboardDigestWeeklyTime || '',
    dashboard_digest_monthly_enabled: !!c?.dashboardDigestMonthlyEnabled,
    dashboard_digest_monthly_day: c?.dashboardDigestMonthlyDay ?? null,
    dashboard_digest_monthly_time: c?.dashboardDigestMonthlyTime || '',
  }
}

// campaign.value gets set from TWO independent places (loadCampaign's own
// fetch, and loadLeads' embedded task) -- syncDigestSettingsFromCampaign()
// used to only be wired to the first one. If that specific fetch ever came
// back empty (a transient error, or this admin not being a personal
// assignee so its own-tasks fallback found nothing) while loadLeads' separate,
// more permissive fetch still succeeded, the rest of the page rendered fine
// off the correct data but this form stayed frozen at its blank starting
// state -- indistinguishable from "digest settings got wiped" even though
// nothing was ever actually lost server-side. Watching the source directly
// keeps this form correct regardless of which loader (or order) populated it.
watch(campaign, () => {
  if (campaign.value) syncDigestSettingsFromCampaign()
})

async function saveDigestSettings() {
  if (!campaignId.value) return
  savingDigest.value = true
  try {
    const response = await editCampaign({
      task_id: campaignId.value,
      ...digestSettings.value,
    })
    if (!isApiSuccess(response)) {
      showSnack('error', getApiMessage(response, 'Could not save Dashboard Digest settings.'))
      return
    }
    await loadCampaign()

    if (digestSendNow.value) {
      const sendResponse = await triggerDashboardDigestNow({
        scope_type: 'CAMPAIGN',
        scope_id: campaignId.value,
      })
      digestSendNow.value = false
      if (!isApiSuccess(sendResponse)) {
        showSnack('error', getApiMessage(sendResponse, 'Settings saved, but the immediate send failed.'))
        return
      }
      showSnack('success', 'Dashboard Digest settings saved and sent.')
      return
    }

    showSnack('success', 'Dashboard Digest settings saved.')
  } catch (err) {
    showSnack('error', getApiMessage(err, 'Could not save Dashboard Digest settings.'))
  } finally {
    savingDigest.value = false
  }
}

function onBulkAssignSaved(result) {
  const message =
    typeof result === 'string'
      ? result
      : result?.message || 'Leads assigned successfully.'
  showSnack(result?.failed ? 'warning' : 'success', message)

  const agentId = String(result?.agentId ?? '').trim()
  const agent = assignableAgents.value.find((item) => String(item.id) === agentId)
  const agentName = humanDisplayLabel(agent?.name, agent?.email)
  const failedIds = new Set(
    (Array.isArray(result?.errors) ? result.errors : []).map((row) =>
      String(row.request_id ?? '').trim(),
    ),
  )
  for (const row of Array.isArray(result?.assignments) ? result.assignments : []) {
    const requestId = String(row.request_id ?? '').trim()
    if (!requestId || failedIds.has(requestId)) continue
    const local = leads.value.find((item) => item.requestId === requestId)
    if (!local) continue
    local.assignedAgentId = agentId
    local.assignedAgentName = agentName
  }

  if (!result?.failed) showBulkAssignDialog.value = false
}

function onBulkStatusSaved(result) {
  const message =
    typeof result === 'string'
      ? result
      : result?.message || 'Lead statuses updated.'
  showSnack(result?.failed || result?.pending ? 'warning' : 'success', message)

  const status = String(result?.requestStatus ?? '').trim().toUpperCase()
  const failedIds = new Set(
    (Array.isArray(result?.errors) ? result.errors : []).map((row) =>
      String(row.request_id ?? '').trim(),
    ),
  )

  if (result?.mode === 'selected' && status) {
    for (const requestId of Array.isArray(result?.requestIds) ? result.requestIds : []) {
      const id = String(requestId ?? '').trim()
      if (!id || failedIds.has(id)) continue
      const local = leads.value.find((item) => item.requestId === id)
      if (!local) continue
      local.status = status
      local.statusLabel = businessQueryStatusLabel(status)
      local.statusTone = businessQueryStatusTone(status)
    }
  } else {
    void refreshAll()
  }

  if (selectedId.value) void loadSelectedLeadHistory()
}

function toggleMute() {
  const next = !callMuted.value
  if (setSoftphoneMuted(next)) callMuted.value = next
}

function sendDtmf(digit) {
  sendSoftphoneDtmf(digit)
}

function endLiveCall() {
  hangupSoftphoneCall()
}

function refreshSoftphoneUi() {
  const wasInCall = callActive.value
  const status = getSoftphoneStatus()
  callActive.value = status.inCall
  callPhase.value = status.phase || 'idle'
  callDurationSec.value = status.durationSec || 0
  callMuted.value = Boolean(status.muted)
  softphoneReady.value = Boolean(status.registered)
  if (!status.inCall) showKeypad.value = false
  if (status.registered && dialError.value.includes('not ready')) {
    dialError.value = ''
  }
  if (
    wasInCall &&
    !status.inCall &&
    showCallDialog.value &&
    activeCallUuid.value &&
    callDialogLead.value
  ) {
    void requestAiCommentSuggestions()
  }
}

async function loadSelectedLeadHistory() {
  const requestId = String(selected.value?.requestId ?? '').trim()
  selectedStatusHistory.value = []
  selectedCallHistory.value = []
  selectedBehaviorProfile.value = ''
  if (!requestId) return

  selectedHistoryLoading.value = true
  try {
    // Scoped to THIS campaign: a lead worked by several campaigns otherwise
    // shows one interleaved log with nothing saying which campaign did what.
    // Status log and call recordings live in separate tables/endpoints — fetch
    // both and let buildLeadActivityFeed() join them by call_uuid, otherwise
    // calls with a real recording render with no player.
    // Behavior profile is deliberately UNSCOPED from campaignId -- it's the
    // same org-wide, cross-campaign record the AI reads at call setup, not
    // this campaign's own history.
    const [statusRes, callRes, profileRes] = await Promise.all([
      getLeadHistory({ request_id: requestId, task_id: campaignId.value }),
      getLeadCallHistory({ request_id: requestId, task_id: campaignId.value }),
      getLeadBehaviorProfile({ request_id: requestId }),
    ])
    if (statusRes && isApiSuccess(statusRes)) {
      selectedStatusHistory.value = normalizeLeadStatusHistoryList(statusRes)
    }
    if (callRes && isApiSuccess(callRes)) {
      selectedCallHistory.value = normalizeLeadCallHistoryList(callRes)
    }
    if (profileRes && isApiSuccess(profileRes)) {
      selectedBehaviorProfile.value = String(
        profileRes.data?.behavior_profile ?? profileRes.behavior_profile ?? '',
      ).trim()
    }
  } finally {
    selectedHistoryLoading.value = false
  }
}

async function loadCampaign() {
  const loaders = isAdmin.value
    ? [fetchCampaigns, fetchMyCampaigns]
    : [fetchMyCampaigns, fetchCampaigns]

  for (const loader of loaders) {
    const response = await loader()
    if (!isApiSuccess(response)) continue
    const list = normalizeCampaignsList(unwrapApiList(response))
    const match = list.find((item) => String(item.campaignId) === campaignId.value)
    if (match) {
      const wasProcessing =
        String(campaign.value?.status ?? '').toUpperCase() === 'PROCESSING'
      campaign.value = match
      syncDigestSettingsFromCampaign()
      scheduleProcessingPoll()
      // Once PROCESSING → ACTIVE, reload leads for the full queue.
      if (
        wasProcessing &&
        String(match.status ?? '').toUpperCase() !== 'PROCESSING'
      ) {
        void loadLeads()
      }
      return
    }
  }
}

async function onCampaignSettingsSaved(message) {
  showSnack('success', message)
  // Re-fetch so the panel re-hydrates from what was actually saved (e.g. a
  // filter change may also affect leads) rather than trusting its own
  // optimistic local state.
  await loadCampaign()
}

function stopProcessingPoll() {
  if (processingPollTimer) {
    clearTimeout(processingPollTimer)
    processingPollTimer = null
  }
}

function scheduleProcessingPoll() {
  stopProcessingPoll()
  if (!isCampaignProcessing.value) return
  processingPollTimer = setTimeout(() => {
    void loadCampaign()
  }, PROCESSING_POLL_MS)
}

async function loadLeads() {
  if (!campaignId.value) return
  loading.value = true
  try {
    const response = await fetchCampaignLeads({
      task_id: campaignId.value,
      all_leads: shouldFetchAllLeads(),
    })
    if (!isApiSuccess(response)) {
      leads.value = []
      showSnack('error', getApiMessage(response, 'Failed to load campaign leads'))
      return
    }
    leads.value = normalizeCampaignLeadsList(unwrapApiList(response))
    const embeddedCampaign = response?.data?.task
    if (embeddedCampaign && typeof embeddedCampaign === 'object') {
      const normalized = normalizeCampaignRecord(embeddedCampaign)
      if (normalized) campaign.value = normalized
    }
    if (!selectedId.value && filteredLeads.value[0]) {
      selectLead(filteredLeads.value[0])
    } else if (selectedId.value) {
      const still = filteredLeads.value.find((lead) => lead.id === selectedId.value)
      if (!still) {
        if (filteredLeads.value[0]) selectLead(filteredLeads.value[0])
        else selectedId.value = ''
      }
    }
  } finally {
    loading.value = false
  }
}

/** Admins on "All leads" send all_leads=true; agents / My leads do not. */
function shouldFetchAllLeads() {
  return isAdmin.value && adminLeadScope.value === 'all'
}

function syncAdminLeadScopeFromRoute() {
  if (!isAdmin.value) {
    adminLeadScope.value = 'mine'
    return
  }
  const q = String(route.query.leads ?? '').trim().toLowerCase()
  adminLeadScope.value = q === 'mine' ? 'mine' : 'all'
}

function onAdminLeadScopeChange() {
  assigneeFilter.value = ''
  selectedId.value = ''
  // Keep URL in sync so refresh preserves scope.
  router.replace({
    name: 'CampaignDetail',
    params: { campaignId: campaignId.value },
    query: { ...route.query, leads: adminLeadScope.value },
  })
  void loadLeads()
}

async function loadContexts() {
  const orgId = String(store.getters.sessionContext?.org_id || '').trim()
  if (!orgId) return
  try {
    const response = await fetchContexts({ org_id: orgId })
    contexts.value = isApiSuccess(response)
      ? normalizeContextsList(unwrapApiList(response))
      : []
  } catch {
    contexts.value = []
  }
}

async function loadAgents() {
  if (!isAdmin.value) return
  try {
    const response = await fetchOrgUsers({})
    agents.value = isApiSuccess(response)
      ? unwrapApiList(response).map(normalizeUserRecord).filter(Boolean)
      : []
  } catch {
    agents.value = []
  }
}

async function ensureOrgFieldDefinitions() {
  const defs = store.state.organization?.default_field_definitions
  if (defs && typeof defs === 'object' && Object.keys(defs).length) return
  try {
    await loadOrgDetailsIntoStore(store)
  } catch {
    /* Customer Information stays empty until org details are available */
  }
}

function refreshAll() {
  void ensureOrgFieldDefinitions()
  void loadLeads()
  void loadCampaign()
}

async function confirmRunAiCalls() {
  if (!campaignId.value) return
  runningAiCalls.value = true
  try {
    const response = await runCampaignAICalls({ task_id: campaignId.value })
    if (!isApiSuccess(response)) {
      showSnack('error', getApiMessage(response, 'Failed to start AI calls'))
      return
    }
    // The toast still carries the one-line summary for anyone who doesn't
    // need the detail, but the real answer to "which ones, and why" only
    // lives in the dialog -- opened automatically rather than behind an
    // extra click, since that's the whole point of this feature existing.
    showSnack('success', getApiMessage(response, 'AI calls started for this campaign.'))
    runAiCallsResult.value = response
    showRunAiCallsResult.value = true
  } finally {
    runningAiCalls.value = false
  }
}

function defaultAssigneeFilter() {
  // Agents don't use the admin assignee dropdown; OPEN shows all task leads.
  if (!isAdmin.value && currentUserId.value && agentSeesAssignedOnly.value) {
    return currentUserId.value
  }
  return ''
}

watch(
  () => selected.value?.requestId,
  () => {
    void loadSelectedLeadHistory()
  },
)

watch(
  [isSessionReady, campaignId],
  ([ready]) => {
    if (!ready || !campaignId.value) return
    leads.value = []
    selectedId.value = ''
    search.value = ''
    statusFilter.value = ''
    assigneeFilter.value = ''
    syncAdminLeadScopeFromRoute()
    void ensureOrgFieldDefinitions()
    void loadCampaign()
    void loadLeads()
    void loadContexts()
    void loadAgents()
  },
  { immediate: true },
)

watch(
  () => route.query.leads,
  () => {
    if (!isSessionReady.value || !campaignId.value || !isAdmin.value) return
    const next = String(route.query.leads ?? '').trim().toLowerCase() === 'mine' ? 'mine' : 'all'
    if (next === adminLeadScope.value) return
    adminLeadScope.value = next
    assigneeFilter.value = ''
    selectedId.value = ''
    void loadLeads()
  },
)

watch([currentUserId, () => campaign.value?.distributionMode], () => {
  if (!isAdmin.value) {
    assigneeFilter.value = defaultAssigneeFilter()
  }
})

onMounted(() => {
  refreshSoftphoneUi()
  softphonePoll = window.setInterval(refreshSoftphoneUi, 400)
})

onBeforeUnmount(() => {
  if (softphonePoll) window.clearInterval(softphonePoll)
  stopProcessingPoll()
})
</script>
